import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { adminAPI, trustAPI, depositAPI, deliveryAPI, couponAPI } from '../services/api';
import { StatusBadge, Spinner, ConfirmDialog } from '../components/shared/index';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// ── SIDEBAR NAV ───────────────────────────────────────────────────────────────
function AdminSidebar({ mobileOpen, onClose }) {
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);

  useEffect(() => {
    // Poll for pending orders every 30s
    const load = () => {
      adminAPI.getStats().then(({ data }) => {
        setPendingCount(data.stats?.pendingOrders || 0);
        setOverdueCount(data.stats?.overdueCount  || 0);
      }).catch(() => {});
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const navItems = [
    { to: '/admin',          label: 'Dashboard', num: '01' },
    { to: '/admin/products', label: 'Products',  num: '02' },
    { to: '/admin/orders',   label: 'Orders',    num: '03', badge: pendingCount + overdueCount },
    { to: '/admin/deposits', label: 'Deposits',  num: '04' },
    { to: '/admin/delivery', label: 'Delivery',  num: '05' },
    { to: '/admin/users',    label: 'Users',     num: '06' },
    { to: '/admin/pickups',  label: 'Pickups',   num: '07' },
    { to: '/admin/coupons',  label: 'Coupons',   num: '08' },
    { to: '/admin/rentals',  label: 'Rentals',   num: '09', badge: overdueCount },
  ];
  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 299, display: 'none' }} className="admin-backdrop" />
      )}
      <aside style={{
        ...s.sidebar,
        position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 300,
        transform: mobileOpen ? 'translateX(0)' : undefined,
      }} className={mobileOpen ? 'admin-sidebar-open' : 'admin-sidebar'}>
        <div style={s.sidebarLogo}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", color: '#fff', fontSize: 17, letterSpacing: '0.08em', lineHeight: 1.1 }}>Warbadobe<br /><span style={{ color: 'var(--gold)' }}>Rentals</span></div>
              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.28em', textTransform: 'uppercase', marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8 }}>Admin Console</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 16, cursor: 'pointer', padding: 4, display: 'none', lineHeight: 1 }} className="sidebar-close-btn">✕</button>
          </div>
        </div>
        <nav>
          {navItems.map(item => {
            const active = location.pathname === item.to || (item.to !== '/admin' && location.pathname.startsWith(item.to));
            return (
              <Link key={item.to} to={item.to} onClick={onClose} style={{ ...s.navItem, ...(active ? s.navActive : {}) }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: active ? 'var(--gold)' : 'rgba(255,255,255,0.25)', letterSpacing: '0.1em', minWidth: 20, fontFamily: 'monospace' }}>{item.num}</span>
                <span style={{ flex: 1, letterSpacing: '0.1em' }}>{item.label}</span>
                {item.badge > 0 && <span style={s.badge}>{item.badge}</span>}
              </Link>
            );
          })}
        </nav>
        <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Link to="/" style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '0.1em' }}>← Back to Store</Link>
        </div>
      </aside>
    </>
  );
}

// ── ADMIN PANEL SHELL ─────────────────────────────────────────────────────────
export function AdminPanel() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [newOrders, setNewOrders] = useState([]);
  const [lastChecked, setLastChecked] = useState(() => {
    try { return localStorage.getItem('admin_last_checked') || new Date(Date.now() - 5 * 60 * 1000).toISOString(); }
    catch { return new Date(Date.now() - 5 * 60 * 1000).toISOString(); }
  });

  // Poll for new orders every 30s
  useEffect(() => {
    const checkNew = () => {
      adminAPI.getAllOrders({ since: lastChecked, limit: 10 })
        .then(({ data }) => {
          const fresh = (data.orders || []).filter(o => new Date(o.createdAt) > new Date(lastChecked));
          if (fresh.length > 0) setNewOrders(fresh);
        })
        .catch(() => {});
    };
    checkNew();
    const iv = setInterval(checkNew, 30000);
    return () => clearInterval(iv);
  }, [lastChecked]); // eslint-disable-line

  const dismissNew = () => {
    const now = new Date().toISOString();
    try { localStorage.setItem('admin_last_checked', now); } catch {}
    setLastChecked(now);
    setNewOrders([]);
  };

  useEffect(() => {
  }, []);
  return (
    <div style={s.panel}>
      <style>{`
        @media (max-width: 768px) {
          .admin-sidebar { transform: translateX(-100%) !important; transition: transform 0.28s ease; }
          .admin-sidebar-open { transform: translateX(0) !important; transition: transform 0.28s ease; }
          .admin-backdrop { display: block !important; }
          .sidebar-close-btn { display: block !important; }
          .admin-main { margin-left: 0 !important; padding: 64px 12px 20px !important; }
          .admin-topbar { display: flex !important; }
          @media(max-width:900px){ .admin-two-col { grid-template-columns:1fr !important; } }
        .admin-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .admin-bottom-grid { grid-template-columns: 1fr !important; }
          .admin-filter-row { flex-direction: column !important; }
          .admin-hide-mobile { display: none !important; }
        }
        @media (min-width: 769px) {
          .admin-sidebar, .admin-sidebar-open { transform: translateX(0) !important; }
          .admin-topbar { display: none !important; }
          .admin-main { margin-left: 240px !important; }
        }
      `}</style>
      <AdminSidebar mobileOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* ── New Order Notification Banner ── */}
      {newOrders.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 999,
          background: 'var(--ink)', borderBottom: '3px solid var(--gold)',
          padding: '0 20px', display: 'flex', alignItems: 'center', gap: 16,
          minHeight: 52, flexWrap: 'wrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        }}>
          <span style={{ fontSize: 10, color: '#ef5350', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', flexShrink: 0 }}>
            ● {newOrders.length} New Order{newOrders.length > 1 ? 's' : ''}
          </span>
          <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '10px 0' }}>
            {newOrders.slice(0, 4).map(o => (
              <span key={o._id} style={{
                fontSize: 11, color: 'rgba(255,255,255,0.85)',
                background: 'rgba(255,255,255,0.08)', padding: '3px 10px',
                border: '1px solid rgba(255,255,255,0.12)',
              }}>
                #{o.orderId} · {o.paymentMethod === 'cod' ? 'COD' : 'Paid'} · ₹{(o.grandTotal || 0).toLocaleString('en-IN')}
              </span>
            ))}
            {newOrders.length > 4 && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>+{newOrders.length - 4} more</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, padding: '10px 0' }}>
            <a href="/admin/orders" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', textDecoration: 'none', padding: '6px 14px', border: '1px solid var(--gold)' }}>
              View Orders
            </a>
            <button onClick={dismissNew} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', background: 'none', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', padding: '6px 14px' }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Mobile top bar */}
      <div className="admin-topbar" style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, background: 'var(--ink)', height: 52, alignItems: 'center', padding: '0 16px', gap: 12 }}>
        <button onClick={() => setMenuOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'block', width: 20, height: 2, background: 'var(--gold)' }} />
          <span style={{ display: 'block', width: 20, height: 2, background: 'var(--gold)' }} />
          <span style={{ display: 'block', width: 20, height: 2, background: 'var(--gold)' }} />
        </button>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", color: 'var(--gold)', fontSize: 17, letterSpacing: '0.08em' }}>Warbadobe Rentals</div>
      </div>
      <main className="admin-main" style={{ flex: 1, padding: 36, minWidth: 0, marginLeft: 240, marginTop: 0 }}>
        {/* Mobile spacer */}
        <div style={{ height: 52 }} className="admin-topbar" />
        <Routes>
          <Route index        element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders"   element={<AdminOrders />} />
          <Route path="deposits"  element={<AdminDepositsPage />} />
          <Route path="delivery"  element={<AdminDeliveryPage />} />
          <Route path="users"    element={<AdminUsers />} />
          <Route path="pickups"  element={<AdminPickups />} />
          <Route path="coupons"  element={<AdminCoupons />} />
          <Route path="rentals"  element={<AdminRentals />} />
        </Routes>
      </main>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function AdminDashboard() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState('30days'); // '30days' | '6months'

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.getStats()
      .then(({ data }) => setStats(data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner size="lg" centered />;
  if (!stats)  return <div className="text-muted text-sm">Failed to load stats.</div>;

  const { stats: st } = stats;

  const kpis = [
    { label: 'Total Orders',     value: st.totalOrders || 0,    color: 'var(--ink)',  sub: `${st.pendingOrders || 0} pending approval` },
    { label: 'Active Rentals',   value: st.activeRentals || 0,  color: '#006064',     sub: 'Currently rented out' },
    { label: 'Total Revenue',    value: `₹${(st.totalRevenue || 0).toLocaleString('en-IN')}`, color: 'var(--gold)', sub: 'All time · paid orders' },
    { label: 'This Month',       value: `₹${(st.thisMonthRevenue || 0).toLocaleString('en-IN')}`, color: 'var(--gold)',
      sub: st.revenueGrowth !== null ? (st.revenueGrowth >= 0 ? `+${st.revenueGrowth}% vs last month` : `${st.revenueGrowth}% vs last month`) : `${st.thisMonthOrders || 0} orders this month`,
      growth: st.revenueGrowth },
    { label: 'Deposits Held',    value: `₹${(st.totalDepositsHeld || 0).toLocaleString('en-IN')}`, color: '#0277bd', sub: 'Security deposits active', link: '/admin/deposits' },
    { label: 'Repeat Customers', value: `${st.repeatRate || 0}%`, color: '#4caf50', sub: `${st.repeatCustomers || 0} of ${st.totalCustomers || 0} customers` },
    { label: 'Overdue Returns',  value: st.overdueCount || 0,   color: st.overdueCount > 0 ? '#b71c1c' : 'var(--muted)', sub: 'Past return date', urgent: st.overdueCount > 0 },
    { label: 'Pending COD',      value: st.pendingCodOrders || 0, color: st.pendingCodOrders > 0 ? '#c45000' : 'var(--muted)', sub: 'Awaiting cash collection', urgent: st.pendingCodOrders > 0, link: '/admin/orders' },
  ];

  // ── Revenue chart data ────────────────────────────────────────────────────────
  const chartData   = chartView === '30days' ? (stats.revenueChart || []) : (stats.monthlyChart || []);
  const maxRevenue  = Math.max(...chartData.map(d => d.revenue), 1);
  const maxOrders   = Math.max(...chartData.map(d => d.orders), 1);
  const totalChartRevenue = chartData.reduce((s, d) => s + d.revenue, 0);
  const totalChartOrders  = chartData.reduce((s, d) => s + d.orders, 0);

  return (
    <div className="fade-up">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={s.pageTitle}>Dashboard</h2>
          <p className="text-muted text-sm mt-4">Operations overview · {format(new Date(), 'dd MMMM yyyy')}</p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <Link to="/admin/orders?status=Pending" style={{ padding:'9px 16px', background:'var(--ink)', color:'var(--gold)', border:'none', fontSize:10, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', cursor:'pointer', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6 }}>
            ⚡ Pending Orders {st.pendingOrders > 0 && <span style={{ background:'#c62828', color:'white', borderRadius:10, padding:'1px 7px', fontSize:9 }}>{st.pendingOrders}</span>}
          </Link>
          <Link to="/admin/rentals" style={{ padding:'9px 16px', background: st.overdueCount > 0 ? '#fff5f5' : 'white', color: st.overdueCount > 0 ? '#b71c1c' : 'var(--ink)', border: st.overdueCount > 0 ? '1px solid #f44336' : '1px solid var(--border)', fontSize:10, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6 }}>
            {st.overdueCount > 0 ? `⚠ ${st.overdueCount} Overdue` : '📋 Active Rentals'}
          </Link>
          <Link to="/admin/products" style={{ padding:'9px 16px', background:'white', color:'var(--ink)', border:'1px solid var(--border)', fontSize:10, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6 }}>
            + Add Product
          </Link>
          <button onClick={load} style={{ padding:'9px 14px', background:'white', color:'var(--muted)', border:'1px solid var(--border)', fontSize:11, cursor:'pointer' }}>↻</button>
        </div>
      </div>

      {/* ── Overdue alert banner ──────────────────────────────────────────────── */}
      {st.overdueCount > 0 && (
        <div style={{ background: '#fff5f5', border: '1px solid #f44336', padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 3, alignSelf: "stretch", background: "#c62828", flexShrink: 0, borderRadius: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#b71c1c' }}>
              {st.overdueCount} Overdue Return{st.overdueCount > 1 ? 's' : ''} — Action Required
            </div>
            <div style={{ fontSize: 12, color: '#c62828', marginTop: 2 }}>
              These rentals are past their return date. Contact customers immediately.
            </div>
          </div>
          <a href="#overdue" style={{ fontSize: 12, color: '#b71c1c', fontWeight: 700, textDecoration: 'none', border: '1px solid #f44336', padding: '6px 14px' }}>
            View Overdue ↓
          </a>
        </div>
      )}

      {/* KPI Grid */}
      <div style={s.kpiGrid} className="admin-kpi-grid">
        {kpis.map(k => (
          <div key={k.label} style={{ ...s.kpiCard, ...(k.urgent ? { borderTop: '2px solid #c62828', background: '#fff8f8' } : k.growth > 0 ? { borderTop: '2px solid #4caf50' } : k.growth < 0 ? { borderTop: '2px solid #f44336' } : { borderTop: '2px solid var(--border)' }) }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 12 }}>
              {k.label}
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px,3.5vw,36px)', color: k.color, lineHeight: 1, marginBottom: 10 }}>
              {k.value}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', borderTop: '1px solid var(--cream-dark)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: k.growth > 0 ? '#2e7d32' : k.growth < 0 ? '#c62828' : 'var(--muted)' }}>
                {k.growth > 0 ? '▲ ' : k.growth < 0 ? '▼ ' : ''}{k.sub}
              </span>
              {k.link && (
                <Link to={k.link} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: k.urgent ? '#b71c1c' : 'var(--gold)', textDecoration: 'none', textTransform: 'uppercase' }}>
                  View
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Low Stock Alert ─────────────────────────────────────────────────── */}
      {stats.lowStock && stats.lowStock.length > 0 && (
        <div style={{ ...s.card, marginBottom:24, borderTop:'2px solid #e65100' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ ...s.cardTitle, color:'#e65100' }}>⚠ Low Stock — Action Required</h3>
            <Link to="/admin/products" style={{ fontSize:10, fontWeight:700, color:'var(--gold)', textDecoration:'none', letterSpacing:'0.1em', textTransform:'uppercase' }}>Manage Products →</Link>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:10 }}>
            {stats.lowStock.map(p => (
              <div key={p._id} style={{ display:'flex', gap:10, alignItems:'center', padding:'10px 12px', border:'1px solid #ffe0cc', background:'#fff8f5', borderRadius:2 }}>
                {p.images?.[0]?.url
                  ? <img src={p.images[0].url} alt={p.name} style={{ width:36, height:44, objectFit:'cover', flexShrink:0 }} />
                  : <div style={{ width:36, height:44, background:'#f5ede4', flexShrink:0 }} />
                }
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize:10, color:'#e65100', marginTop:2 }}>
                    {Object.entries(p.stockBySize || {}).filter(([,v]) => v <= 2 && v >= 0).map(([size, qty]) => (
                      <span key={size} style={{ marginRight:6 }}>{size}: <strong>{qty}</strong></span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Revenue Chart ─────────────────────────────────────────────────────── */}
      <div style={{ ...s.card, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={s.cardTitle}>Revenue Overview</h3>
            <div style={{ display: 'flex', gap: 24, marginTop: 4 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                Total: <strong style={{ color: 'var(--gold)' }}>₹{totalChartRevenue.toLocaleString('en-IN')}</strong>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                Orders: <strong style={{ color: 'var(--ink)' }}>{totalChartOrders}</strong>
              </div>
            </div>
          </div>
          <div className="coupon-action-btns" style={{ display: 'flex', gap: 6 }}>
            {[['30days', 'Last 30 Days'], ['6months', 'Last 6 Months']].map(([v, l]) => (
              <button key={v} onClick={() => setChartView(v)}
                style={{ fontSize: 11, padding: '5px 12px', border: `1px solid ${chartView === v ? 'var(--gold)' : 'var(--border)'}`, background: chartView === v ? 'rgba(184,146,74,0.1)' : 'white', color: chartView === v ? 'var(--gold)' : 'var(--muted)', cursor: 'pointer', fontWeight: chartView === v ? 700 : 400 }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {chartData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 13 }}>
            No revenue data for this period yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {/* Bar chart — pure CSS/HTML, no library needed */}
            <div style={{ minWidth: chartView === '30days' ? 600 : 300 }}>
              {/* Revenue bars */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: chartView === '30days' ? 4 : 12, height: 140, paddingBottom: 0 }}>
                {chartData.map((d, i) => {
                  const h = Math.round((d.revenue / maxRevenue) * 120);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}
                      title={`${d.label}\n₹${d.revenue.toLocaleString('en-IN')}\n${d.orders} order${d.orders !== 1 ? 's' : ''}`}>
                      {d.revenue > 0 && (
                        <div style={{ fontSize: 8, color: 'var(--gold)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {d.revenue >= 1000 ? `₹${Math.round(d.revenue/1000)}k` : `₹${d.revenue}`}
                        </div>
                      )}
                      <div style={{ width: '100%', height: h || 2, background: d.revenue > 0 ? 'var(--gold)' : 'var(--border)', minHeight: 2, transition: 'height 0.3s ease' }} />
                    </div>
                  );
                })}
              </div>
              {/* X axis labels */}
              <div style={{ display: 'flex', gap: chartView === '30days' ? 4 : 12, marginTop: 6, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
                {chartData.map((d, i) => (
                  <div key={i} style={{ flex: 1, fontSize: chartView === '30days' ? 8 : 10, color: 'var(--muted)', textAlign: 'center', overflow: 'hidden' }}>
                    {chartView === '30days'
                      ? (i % 5 === 0 ? d.label : '')
                      : d.label
                    }
                  </div>
                ))}
              </div>
              {/* Orders sparkline */}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-end', gap: chartView === '30days' ? 4 : 12, height: 40 }}>
                {chartData.map((d, i) => {
                  const h = Math.round((d.orders / maxOrders) * 36);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ width: '100%', height: h || 2, background: d.orders > 0 ? '#90caf9' : 'var(--border)', minHeight: 2 }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--muted)' }}>
                  <div style={{ width: 12, height: 10, background: 'var(--gold)' }} /> Revenue
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--muted)' }}>
                  <div style={{ width: 12, height: 10, background: '#90caf9' }} /> Orders
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Overdue Orders ────────────────────────────────────────────────────── */}
      {stats.overdueOrders?.length > 0 && (
        <div id="overdue" style={{ ...s.card, marginBottom: 24, border: '1px solid #f44336' }}>
          <h3 style={{ ...s.cardTitle, color: '#b71c1c', display: 'flex', alignItems: 'center', gap: 8 }}>
            Overdue Returns
            <span style={{ fontSize: 11, background: '#f44336', color: 'white', padding: '2px 8px', fontFamily: 'monospace' }}>
              {stats.overdueOrders.length}
            </span>
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['Order ID', 'Customer', 'Phone', 'Product', 'Return Date', 'Days Overdue', 'Status'].map(h => (
                  <th key={h} style={{ ...s.th, background: '#fff5f5' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {stats.overdueOrders.map(order => {
                  const item      = order.items?.[0];
                  const returnDate = item?.returnDate ? new Date(item.returnDate) : null;
                  const daysOver  = returnDate ? Math.floor((new Date() - returnDate) / (1000 * 60 * 60 * 24)) : 0;
                  return (
                    <tr key={order._id} style={{ background: daysOver > 3 ? '#fff5f5' : 'white' }}>
                      <td style={s.td}><span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>#{order.orderId}</span></td>
                      <td style={s.td}><div style={{ fontWeight: 500, fontSize: 13 }}>{order.user?.name}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>{order.user?.email}</div></td>
                      <td style={s.td}><span style={{ fontSize: 12 }}>{order.user?.phone ? `+91${order.user.phone}` : '—'}</span></td>
                      <td style={s.td}><span style={{ fontSize: 12 }}>{item?.productSnapshot?.name || '—'}</span></td>
                      <td style={s.td}><span style={{ fontSize: 12, color: '#b71c1c', fontWeight: 600 }}>{returnDate ? format(returnDate, 'dd MMM yyyy') : '—'}</span></td>
                      <td style={s.td}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: daysOver > 3 ? '#b71c1c' : '#e65100' }}>
                          {daysOver} day{daysOver !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td style={s.td}>
                    <div style={{ fontSize:11, color:'var(--muted)', maxWidth:130, lineHeight:1.5 }}>
                      {order.shippingAddress ? (
                        <>
                          <div style={{ fontWeight:600, color:'var(--ink)' }}>{order.shippingAddress.city || '—'}</div>
                          {order.shippingAddress.hostel && <div style={{ fontSize:9, color:'var(--gold)' }}>🎓 {order.shippingAddress.hostel}</div>}
                          <div>{order.shippingAddress.phone}</div>
                        </>
                      ) : '—'}
                    </div>
                  </td>
                  <td style={s.td}><StatusBadge status={order.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Low Stock Alert ──────────────────────────────────────────────────────── */}
      {stats.lowStockProducts?.length > 0 && (
        <div style={{ ...s.card, marginBottom: 24, borderTop: '2px solid #f44336' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ ...s.cardTitle, color: '#b71c1c', margin: 0 }}>⚠ Low Stock Alert</h3>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Active products with one or more sizes at 0 or 1 unit</p>
            </div>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: '#f44336' }}>{stats.lowStockProducts.length}</span>
          </div>
          <div className="low-stock-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {stats.lowStockProducts.map(p => {
              const lowSizes = Object.entries(p.stockBySize || {}).filter(([sz, qty]) => p.sizes?.includes(sz) && qty <= 1);
              return (
                <div key={p._id} style={{ display: 'flex', gap: 12, padding: '12px 14px', border: '1px solid #ffcdd2', background: '#fff5f5', alignItems: 'flex-start' }}>
                  {p.images?.[0]?.url && <img src={p.images[0].url} alt={p.name} style={{ width: 36, height: 44, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>{p.category}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {lowSizes.map(([sz, qty]) => (
                        <span key={sz} style={{ padding: '2px 7px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: qty === 0 ? '#f44336' : '#ff9800', color: 'white' }}>
                          {sz}: {qty === 0 ? 'OUT' : qty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Revenue by Category ────────────────────────────────────────────────── */}
      {stats.revenueByCategory?.length > 0 && (() => {
        const maxCatRev = Math.max(...(stats.revenueByCategory || []).map(c => c.revenue), 1);
        return (
          <div style={{ ...s.card, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={s.cardTitle}>Revenue by Category</h3>
              <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '0.1em' }}>Last 30 days</div>
            </div>
            <div className="revenue-cat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {(stats.revenueByCategory || []).map((cat, i) => {
                const pct = Math.round((cat.revenue / maxCatRev) * 100);
                const colors = ['var(--gold)','#2196f3','#9c27b0','#4caf50','#ff5722','#00bcd4','#ff9800','#e91e63','#607d8b'];
                return (
                  <div key={cat._id} style={{ background: 'var(--cream)', padding: '12px 16px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--ink)' }}>{cat._id || 'Uncategorised'}</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>{cat.orders} orders</span>
                    </div>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: colors[i % colors.length], marginBottom: 8 }}>
                      ₹{cat.revenue.toLocaleString('en-IN')}
                    </div>
                    <div style={{ background: 'var(--border)', height: 4, borderRadius: 2 }}>
                      <div style={{ width: pct + '%', height: '100%', background: colors[i % colors.length], borderRadius: 2, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Upcoming Deliveries (next 7 days) ────────────────────────────────── */}
      {stats.upcomingDeliveries?.length > 0 && (
        <div style={{ ...s.card, marginBottom: 24, borderTop: '2px solid #2196f3' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <h3 style={{ ...s.cardTitle, margin: 0 }}>Upcoming Deliveries</h3>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Orders to be dispatched in the next 7 days</p>
            </div>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: '#2196f3' }}>{stats.upcomingDeliveries.length}</span>
          </div>
          <div className="upcoming-deliveries-table" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['Order', 'Customer', 'Phone', 'Address', 'Delivery Date', 'Status'].map(h => (
                    <th key={h} style={{ ...s.th, background: '#e3f2fd' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.upcomingDeliveries.map(order => {
                  const delivDate = order.items?.[0]?.deliveryDate;
                  const daysLeft  = delivDate ? Math.ceil((new Date(delivDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  const isToday   = daysLeft === 0;
                  const isTomorrow= daysLeft === 1;
                  return (
                    <tr key={order._id} style={{ borderBottom: '1px solid var(--cream-dark)', background: isToday ? '#e3f2fd' : 'white' }}>
                      <td style={s.td}>
                        <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>#{order.orderId}</div>
                        <StatusBadge status={order.status} />
                      </td>
                      <td style={s.td}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{order.user?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{order.user?.email}</div>
                      </td>
                      <td style={s.td}>
                        <div style={{ fontSize: 12 }}>{order.user?.phone ? `+91 ${order.user.phone}` : order.shippingAddress?.phone || '—'}</div>
                      </td>
                      <td style={{ ...s.td, maxWidth: 180 }}>
                        {order.shippingAddress ? (
                          <div style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.6 }}>
                            {order.shippingAddress.street}<br />
                            <span style={{ color: 'var(--muted)' }}>{order.shippingAddress.city} — {order.shippingAddress.pincode}</span>
                          </div>
                        ) : '—'}
                      </td>
                      <td style={s.td}>
                        {delivDate && (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 700, color: isToday ? '#1565c0' : isTomorrow ? '#e65100' : 'var(--ink)' }}>
                              {format(new Date(delivDate), 'dd MMM yyyy')}
                            </div>
                            <div style={{ fontSize: 10, color: isToday ? '#1565c0' : isTomorrow ? '#e65100' : 'var(--muted)', fontWeight: isToday || isTomorrow ? 700 : 400 }}>
                              {isToday ? 'TODAY' : isTomorrow ? 'Tomorrow' : `${daysLeft} days`}
                            </div>
                          </>
                        )}
                      </td>
                      <td style={s.td}>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {order.items?.map((item, i) => (
                            <div key={i}>{item.productSnapshot?.name} <span style={{ color: 'var(--muted)' }}>({item.size})</span></div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Top Products by Revenue + Orders by Status + Upcoming Returns ────── */}
      <div className="admin-bottom-grid admin-form-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* Top products by revenue */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Top Products</h3>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 12 }}>By rental revenue · all time</p>
          {(stats.topProductsByRevenue || stats.topProducts || []).map((p, i) => {
            const name    = p.name || p.name;
            const image   = p.image || p.images?.[0]?.url;
            const revenue = p.revenue;
            const rentals = p.rentals || p.totalRentals || 0;
            return (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', minWidth: 18, fontFamily: 'monospace' }}>{i + 1}</span>
                {image && <img src={image} alt={name} style={{ width: 30, height: 36, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                    {revenue > 0 && <div style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 600 }}>₹{revenue.toLocaleString('en-IN')}</div>}
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{rentals} rentals</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Orders by status */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Orders by Status</h3>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 12 }}>All time breakdown</p>
          {stats.ordersByStatus?.map(row => {
            const pct = st.totalOrders > 0 ? Math.round((row.count / st.totalOrders) * 100) : 0;
            return (
              <div key={row._id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <StatusBadge status={row._id} />
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>{row.count}</span>
                </div>
                <div style={{ background: 'var(--cream-dark)', height: 3, borderRadius: 2 }}>
                  <div style={{ width: pct + '%', height: '100%', background: 'var(--gold)', borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Upcoming returns */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Upcoming Returns</h3>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 12 }}>Next 7 days</p>
          {stats.upcomingReturns?.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '20px 0' }}>None this week.</div>
          )}
          {stats.upcomingReturns?.map(order => {
            const returnDate = order.items?.[0]?.returnDate;
            const daysLeft   = returnDate ? Math.ceil((new Date(returnDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
            return (
              <div key={order._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>#{order.orderId}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{order.user?.name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {returnDate && <div style={{ fontSize: 11, fontWeight: 600, color: daysLeft <= 1 ? '#b71c1c' : '#e65100' }}>{format(new Date(returnDate), 'dd MMM')}</div>}
                  {daysLeft !== null && <div style={{ fontSize: 10, color: 'var(--muted)' }}>{daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft} days`}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Products + Recent Orders */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }} className="admin-two-col">
        <div style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={s.cardTitle}>Top Products</h3>
            <Link to="/admin/products" style={{ fontSize:10, color:'var(--gold)', textDecoration:'none', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>View All</Link>
          </div>
          {stats.topProducts?.map((p, i) => (
            <div key={p._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--muted)', width:16, flexShrink:0 }}>{i+1}</div>
              {p.images?.[0]?.url
                ? <img src={p.images[0].url} alt={p.name} style={{ width:32, height:40, objectFit:'cover', flexShrink:0 }} />
                : <div style={{ width:32, height:40, background:'var(--cream)', flexShrink:0 }} />
              }
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                <div style={{ fontSize:10, color:'var(--muted)' }}>₹{p.rentalPrices?.twoDays?.toLocaleString('en-IN')} · {p.totalRentals} rentals</div>
              </div>
            </div>
          ))}
        </div>

      {/* Recent Orders */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Recent Orders</h3>
        {stats.recentOrders?.map(order => (
          <div key={order._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>#{order.orderId}</span>
                {order.orderType === 'purchase' && <TypeBadge type="purchase" />}
                <PayMethodBadge method={order.payment?.method} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                {order.user?.name} · ₹{order.grandTotal?.toLocaleString('en-IN')}
              </div>
            </div>
            <StatusBadge status={order.status} />
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              {order.createdAt ? format(new Date(order.createdAt), 'dd MMM') : ''}
            </div>
          </div>
        ))}
      </div>
      </div>{/* end two-col */}

    </div>
  );
}

// ── WHATSAPP MESSAGE SENDER ──────────────────────────────────────────────────
function WhatsAppMessageSender({ phone, name }) {
  const [msg, setMsg] = useState('');
  const firstName = name?.split(' ')[0] || '';

  const TEMPLATES = [
    { label: 'Order Ready', text: `Hi ${firstName}! Your Warbadobe order is ready and will be delivered soon. Please keep the payment ready if COD.` },
    { label: 'Return Reminder', text: `Hi ${firstName}! This is a friendly reminder from Warbadobe — please return your outfit by the due date to get your full deposit back.` },
    { label: 'Overdue Alert', text: `Hi ${firstName}, your Warbadobe rental is past its return date. Please schedule a return pickup ASAP to avoid deposit deductions.` },
    { label: 'Deposit Refunded', text: `Hi ${firstName}! Your Warbadobe security deposit has been refunded. Thank you for renting with us!` },
  ];

  const cleanPhone = phone.replace(/[^0-9]/g, '');
  const send = () => {
    if (!msg.trim()) return;
    window.open(`https://wa.me/91${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#15803d', marginBottom: 10 }}>
        📱 Send WhatsApp Message
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {TEMPLATES.map(t => (
          <button key={t.label} onClick={() => setMsg(t.text)}
            style={{ padding: '4px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'white', border: '1px solid #bbf7d0', color: '#15803d', cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>
      <textarea value={msg} onChange={e => setMsg(e.target.value)}
        placeholder="Type a custom message or pick a template above..."
        style={{ width: '100%', padding: '8px 10px', fontSize: 12, border: '1px solid #bbf7d0', resize: 'vertical', minHeight: 72, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
      <button onClick={send} disabled={!msg.trim()}
        style={{ marginTop: 8, padding: '8px 20px', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', background: msg.trim() ? '#25D366' : '#e5e7eb', color: msg.trim() ? 'white' : '#9ca3af', border: 'none', cursor: msg.trim() ? 'pointer' : 'not-allowed' }}>
        Open WhatsApp →
      </button>
    </div>
  );
}

// ── ADMIN ORDERS ──────────────────────────────────────────────────────────────
function AdminOrders() {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [total, setTotal]         = useState(0);
  const [selected, setSelected]   = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [note, setNote]           = useState('');
  const [updating, setUpdating]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search so we don't fire on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 380);
    return () => clearTimeout(t);
  }, [search]);

  const getTransitions = (order) => {
    if (order.orderType === 'purchase') {
      return ({ 'Pending':['Approved','Rejected'], 'Approved':['Shipped'], 'Shipped':['Delivered'], 'Delivered':['Completed'] })[order.status] || [];
    }
    return ({ 'Pending':['Approved','Rejected'], 'Approved':['Shipped'], 'Shipped':['Delivered'], 'Delivered':['Active Rental'], 'Active Rental':['Return Requested'], 'Return Requested':['Returned'], 'Returned':['Completed'] })[order.status] || [];
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (methodFilter) params.method = methodFilter;
      if (typeFilter)   params.type   = typeFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await adminAPI.getAllOrders(params);
      setOrders(data.orders);
      setTotal(data.total);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [statusFilter, methodFilter, typeFilter, search]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusUpdate = async () => {
    if (!newStatus) { toast.error('Select a status'); return; }
    setUpdating(true);
    try {
      const { data } = await adminAPI.updateOrderStatus(selected._id, { status: newStatus, note });
      toast.success('Order updated to ' + data.order.status);
      if (newStatus === 'Delivered' && selected.payment?.method === 'cod') {
        toast.success('COD payment marked as collected.', { duration: 4000 });
      }
      setSelected(null); fetchOrders();
    } catch (e) { toast.error(e.response?.data?.message || 'Update failed'); }
    finally { setUpdating(false); }
  };

  const clearFilters = () => { setStatusFilter(''); setMethodFilter(''); setTypeFilter(''); setSearch(''); };
  const hasFilter = statusFilter || methodFilter || typeFilter || search;

  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (methodFilter) params.method = methodFilter;
      if (typeFilter)   params.type   = typeFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await adminAPI.exportOrders(params);
      const url  = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `warbadobe-orders-${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const STATUSES = ['', 'Pending', 'Approved', 'Shipped', 'Delivered', 'Active Rental', 'Return Requested', 'Returned', 'Completed', 'Rejected'];

  return (
    <div className="fade-up">

      {/* ── Page header ── */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid var(--border)', paddingBottom: 20 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>Warbadobe Rentals</div>
          <h2 style={s.pageTitle}>Orders</h2>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={handleExport}
            style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            ↓ Export CSV
          </button>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: 'var(--ink)', lineHeight: 1 }}>{total}</div>
            <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 3 }}>{hasFilter ? 'Filtered Results' : 'Total Orders'}</div>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ background: 'white', border: '1px solid var(--border)', marginBottom: 24, borderTop: '2px solid var(--ink)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--ink)' }}>Filters</div>
          {hasFilter && (
            <button onClick={clearFilters} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
              Clear All
            </button>
          )}
        </div>

        {/* Status row */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--cream-dark)' }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Status</div>
          <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap', gap: '4px' }}>
            {STATUSES.map(st => (
              <button key={st} onClick={() => setStatusFilter(st)} style={{
                padding: '5px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                border: '1px solid ' + (statusFilter === st ? 'var(--ink)' : 'var(--border)'),
                background: statusFilter === st ? 'var(--ink)' : 'transparent',
                color: statusFilter === st ? 'var(--cream)' : 'var(--muted)',
                cursor: 'pointer', transition: 'all 0.15s', textTransform: 'uppercase',
              }}>
                {st || 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Payment + Type + Search */}
        <div className="admin-filter-row" style={{ padding: '14px 20px', display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Payment</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[['','All'],['cod','COD'],['razorpay','Online']].map(([v,l]) => (
                <button key={v} onClick={() => setMethodFilter(v)} style={{
                  padding: '5px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                  border: '1px solid ' + (methodFilter === v ? 'var(--ink)' : 'var(--border)'),
                  background: methodFilter === v ? 'var(--ink)' : 'transparent',
                  color: methodFilter === v ? 'var(--cream)' : 'var(--muted)',
                  cursor: 'pointer', transition: 'all 0.15s', textTransform: 'uppercase',
                }}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Type</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[['','All'],['rental','Rental'],['purchase','Purchase']].map(([v,l]) => (
                <button key={v} onClick={() => setTypeFilter(v)} style={{
                  padding: '5px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                  border: '1px solid ' + (typeFilter === v ? 'var(--ink)' : 'var(--border)'),
                  background: typeFilter === v ? 'var(--ink)' : 'transparent',
                  color: typeFilter === v ? 'var(--cream)' : 'var(--muted)',
                  cursor: 'pointer', transition: 'all 0.15s', textTransform: 'uppercase',
                }}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Search</div>
            <input className="input" style={{ width: 'clamp(160px, 30vw, 220px)', fontSize: 12 }} placeholder="Order ID, name, email or phone..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? <Spinner size="lg" centered /> : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', background: 'white', border: '1px solid var(--border)' }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontStyle: 'italic', fontWeight: 300, color: 'var(--cream-dark)', marginBottom: 8 }}>No orders found</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Try adjusting your filters</div>
        </div>
      ) : (
        <div className="admin-scrolltable" style={{ background: 'white', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Order', 'Customer', 'Type · Payment', 'Delivery', 'Status', 'Amount', 'Actions'].map(h => (
                  <th key={h} style={{ ...s.th, background: 'var(--cream)', borderBottom: '2px solid var(--cream-dark)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => {
                const isCodPending = order.payment?.method === 'cod' && order.payment?.status === 'pending_cod';
                return (
                  <tr key={order._id}
                    onClick={() => { setSelected(order); setNewStatus(''); setNote(''); }}
                    style={{ borderBottom: '1px solid var(--cream-dark)', cursor: 'pointer', background: isCodPending ? '#fffbf0' : idx % 2 === 0 ? 'white' : '#fdfcfb', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-pale)'}
                    onMouseLeave={e => e.currentTarget.style.background = isCodPending ? '#fffbf0' : idx % 2 === 0 ? 'white' : '#fdfcfb'}>

                    <td style={{ ...s.td, minWidth: 120 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--ink)', letterSpacing: '0.04em' }}>#{order.orderId}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>
                        {order.createdAt ? format(new Date(order.createdAt), 'dd MMM yyyy') : ''}
                      </div>
                    </td>

                    <td style={{ ...s.td, minWidth: 150 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{order.user?.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{order.user?.email}</div>
                      {order.user?.isCollegeVerified
                        ? <span style={{ display: 'inline-block', marginTop: 3, padding: '1px 6px', fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#e8f5e9', border: '1px solid #4caf50', color: '#1b5e20' }}>🎓 College</span>
                        : <span style={{ display: 'inline-block', marginTop: 3, padding: '1px 6px', fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#fafafa', border: '1px solid #e0e0e0', color: '#9e9e9e' }}>Standard</span>
                      }
                    </td>

                    <td style={{ ...s.td, minWidth: 140 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <TypeBadge type={order.orderType || 'rental'} />
                        <div style={{ display: 'flex', gap: 4 }}>
                          <PayMethodBadge method={order.payment?.method} />
                          <PayStatusBadge status={order.payment?.status} />
                        </div>
                      </div>
                    </td>

                    <td style={{ ...s.td, minWidth: 130 }}>
                      <div style={{ fontSize:11, color:'var(--ink)', lineHeight:1.5 }}>
                        {order.shippingAddress?.deliveryType === 'campus' ? (
                          <div>
                            <div style={{ fontSize:8, fontWeight:800, color:'var(--gold)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:2 }}>🎓 Campus</div>
                            <div style={{ fontSize:10, color:'var(--muted)' }}>{order.shippingAddress?.hostel || order.shippingAddress?.city}</div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight:600, fontSize:11 }}>{order.shippingAddress?.city || '—'}</div>
                            <div style={{ fontSize:10, color:'var(--muted)' }}>{order.shippingAddress?.phone}</div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ ...s.td, minWidth: 120 }}>
                      <StatusBadge status={order.status} />
                    </td>

                    <td style={{ ...s.td, minWidth: 110 }}>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: 'var(--ink)', fontWeight: 400, lineHeight: 1 }}>
                        ₹{order.grandTotal?.toLocaleString('en-IN')}
                      </div>
                      {order.totalDeposit > 0 && order.orderType !== 'purchase' && (
                        <div style={{ fontSize: 10, color: '#0277bd', marginTop: 2 }}>+₹{order.totalDeposit?.toLocaleString('en-IN')} dep.</div>
                      )}
                      {order.deliveryFee > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>incl. ₹{order.deliveryFee} delivery</div>
                      )}
                    </td>

                    <td style={{ ...s.td, minWidth: 120 }} onClick={e => e.stopPropagation()}>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        {/* Quick approve for Pending orders */}
                        {order.status === 'Pending' && (
                          <div style={{ display:'flex', gap:4 }}>
                            <button onClick={async () => { try { await adminAPI.updateOrderStatus(order._id,{status:'Approved'}); toast.success('Approved!'); fetchOrders(); } catch(e){toast.error(e.response?.data?.message||'Failed');} }}
                              style={{ flex:1, padding:'5px 0', fontSize:9, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', background:'#1b5e20', color:'white', border:'none', cursor:'pointer' }}>
                              ✓ Approve
                            </button>
                            <button onClick={async () => { if(!window.confirm('Reject this order?')) return; try { await adminAPI.updateOrderStatus(order._id,{status:'Rejected'}); toast.success('Rejected'); fetchOrders(); } catch(e){toast.error(e.response?.data?.message||'Failed');} }}
                              style={{ flex:1, padding:'5px 0', fontSize:9, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', background:'#b71c1c', color:'white', border:'none', cursor:'pointer' }}>
                              ✗ Reject
                            </button>
                          </div>
                        )}
                        <button
                          onClick={() => { setSelected(order); setNewStatus(''); setNote(''); }}
                          style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', background:'none', border:'1px solid var(--border)', color:'var(--ink)', padding:'5px 10px', cursor:'pointer' }}>
                          Open Details
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Order Detail — side drawer ── */}
      {selected && (
        <div style={sp.overlay} onClick={() => setSelected(null)}>
          <div className="admin-drawer" style={{ ...sp.drawer, maxWidth: 600 }} onClick={e => e.stopPropagation()}>

            {/* Drawer header */}
            <div style={{ padding: '0 0 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ background: 'var(--ink)', padding: '20px 28px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>Order</div>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: '#fff', fontWeight: 300, lineHeight: 1 }}>#{selected.orderId}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>{selected.user?.name} · {selected.user?.email}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: 'var(--gold)', fontWeight: 300 }}>₹{selected.grandTotal?.toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 4 }}>Grand Total</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <StatusBadge status={selected.status} />
                  <TypeBadge type={selected.orderType || 'rental'} />
                  <PayMethodBadge method={selected.payment?.method} />
                  <PayStatusBadge status={selected.payment?.status} />
                </div>
              </div>

              <div style={{ padding: '0 28px', display:'flex', gap:10, alignItems:'center' }}>
                <button onClick={() => setSelected(null)}
                  style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 0 }}>
                  ← Close
                </button>
                {selected.user?.phone && (
                  <a href={`https://wa.me/91${selected.user.phone.replace(/[^0-9]/g,'')}?text=Hi%20${encodeURIComponent(selected.user?.name?.split(' ')[0] || '')}%2C%20this%20is%20Warbadobe%20regarding%20your%20order%20%23${selected.orderId}.`}
                    target="_blank" rel="noreferrer"
                    style={{ fontSize:9, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'#25D366', textDecoration:'none', border:'1px solid #25D366', padding:'5px 12px', display:'inline-flex', alignItems:'center', gap:5 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp Customer
                  </a>
                )}
              </div>
            </div>

            {/* Drawer body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

              {/* COD notices */}
              {selected.payment?.method === 'cod' && selected.status === 'Shipped' && selected.orderType !== 'purchase' && (
                <Notice color="#c45000"><strong>COD Rental</strong> — Marking as Delivered collects payment and moves to Active Rental.</Notice>
              )}
              {selected.payment?.method === 'cod' && selected.status === 'Shipped' && selected.orderType === 'purchase' && (
                <Notice color="#2d6a2d"><strong>COD Purchase</strong> — Marking as Delivered collects payment and completes the order.</Notice>
              )}

              {/* Items */}
              <div style={{ marginBottom: 24 }}>
                <SectionTitle>Items</SectionTitle>
                {selected.items?.map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--cream-dark)' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>{item.productSnapshot?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>Size {item.size} · {item.rentalDurationLabel}</div>
                      {item.returnDate && item.rentalDuration !== 'purchase' && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Return by {format(new Date(item.returnDate), 'dd MMM yyyy')}</div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: 'var(--ink)' }}>₹{item.rentalPrice?.toLocaleString('en-IN')}</div>
                      {item.securityDeposit > 0 && <div style={{ fontSize: 10, color: '#0277bd' }}>+₹{item.securityDeposit?.toLocaleString('en-IN')} dep.</div>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', padding: '14px 18px', marginBottom: 24 }}>
                {[
                  ['Subtotal', '₹' + selected.subtotal?.toLocaleString('en-IN'), null],
                  ...(selected.totalDeposit > 0 ? [['Security Deposit', '₹' + selected.totalDeposit?.toLocaleString('en-IN'), '#0277bd']] : []),
                ].map(([label, value, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: 'var(--muted)' }}>{label}</span>
                    <span style={{ color: color || 'var(--ink)' }}>{value}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Total</span>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: 'var(--ink)', fontWeight: 400 }}>₹{selected.grandTotal?.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Shipping */}
              {selected.shippingAddress?.name && (
                <div style={{ marginBottom: 24 }}>
                  <SectionTitle>Delivery Address</SectionTitle>
                  <div style={{ background:'var(--cream)', border:'1px solid var(--border)', padding:'12px 16px', fontSize:12, color:'var(--ink)', lineHeight:1.9 }}>
                    {selected.shippingAddress.deliveryType === 'campus' && (
                      <div style={{ fontSize:9, fontWeight:800, color:'var(--gold)', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:6 }}>🎓 Campus Delivery</div>
                    )}
                    <strong>{selected.shippingAddress.name}</strong><br />
                    {selected.shippingAddress.hostel && <span>{selected.shippingAddress.hostel} Hostel<br /></span>}
                    {selected.shippingAddress.roomNumber && <span>Room {selected.shippingAddress.roomNumber}<br /></span>}
                    {selected.shippingAddress.street && <span>{selected.shippingAddress.street}, </span>}
                    {selected.shippingAddress.city}<br />
                    {selected.shippingAddress.pincode && <span>{selected.shippingAddress.pincode} · </span>}
                    <a href={`tel:${selected.shippingAddress.phone}`} style={{ color:'var(--gold)', fontWeight:700 }}>{selected.shippingAddress.phone}</a>
                  </div>
                </div>
              )}

              {/* Extension request */}
              {selected.extensionRequest?.status === 'pending' && (
                <div style={{ marginBottom: 20 }}>
                  <SectionTitle>Extension Request</SectionTitle>
                  <Notice color="#c45000">
                    Customer requests <strong>+{selected.extensionRequest.extraDays} day(s)</strong> · Extra charge: <strong>₹{selected.extensionRequest.extraCharge?.toLocaleString('en-IN')}</strong>
                    {selected.extensionRequest.note && <div style={{ marginTop: 4, fontStyle: 'italic' }}>{selected.extensionRequest.note}</div>}
                  </Notice>
                  <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <button style={{ flex: 1, padding: '9px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer' }}
                      onClick={async () => { try { await adminAPI.handleExtension(selected._id, { action: 'approve' }); toast.success('Extension approved'); setSelected(null); fetchOrders(); } catch(e) { toast.error(e.response?.data?.message || 'Failed'); } }}>
                      Approve
                    </button>
                    <button style={{ flex: 1, padding: '9px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'none', color: '#c62828', border: '1px solid #c62828', cursor: 'pointer' }}
                      onClick={async () => { const reason = window.prompt('Rejection reason (optional):') || ''; try { await adminAPI.handleExtension(selected._id, { action: 'reject', adminNote: reason }); toast.success('Extension rejected'); setSelected(null); fetchOrders(); } catch(e) { toast.error(e.response?.data?.message || 'Failed'); } }}>
                      Reject
                    </button>
                  </div>
                </div>
              )}
              {selected.extensionRequest?.status === 'approved' && (
                <Notice color="#2d6a2d" style={{ marginBottom: 20 }}>Extension approved: +{selected.extensionRequest.extraDays} day(s). Extra ₹{selected.extensionRequest.extraCharge?.toLocaleString('en-IN')}</Notice>
              )}

              {/* COD deposit */}
              {selected.payment?.method === 'cod' && ['Delivered','Active Rental'].includes(selected.status) && !selected.payment?.codDepositCollectedAt && selected.totalDeposit > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <SectionTitle>Security Deposit</SectionTitle>
                  <Notice color="#c45000">Cash deposit of ₹{selected.totalDeposit?.toLocaleString('en-IN')} not yet collected.</Notice>
                  <button style={{ marginTop: 12, width: '100%', padding: '10px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}
                    onClick={async () => { try { await adminAPI.markCodDepositCollected(selected._id, { amountCollected: selected.totalDeposit }); toast.success('Deposit marked as collected'); setSelected(null); fetchOrders(); } catch(e) { toast.error(e.response?.data?.message || 'Failed'); } }}>
                    Mark ₹{selected.totalDeposit?.toLocaleString('en-IN')} Collected
                  </button>
                </div>
              )}
              {selected.payment?.codDepositCollectedAt && (
                <Notice color="#2d6a2d" style={{ marginBottom: 20 }}>Deposit of ₹{selected.payment.codDepositAmount?.toLocaleString('en-IN')} collected on {format(new Date(selected.payment.codDepositCollectedAt), 'dd MMM yyyy')}</Notice>
              )}

              {/* Status update */}
              <div style={{ marginBottom: 24 }}>
                <SectionTitle>Update Status</SectionTitle>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                  Current: <StatusBadge status={selected.status} />
                </div>
                {getTransitions(selected).length > 0 ? (
                  <>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                      {getTransitions(selected).map(st => (
                        <button key={st} onClick={() => setNewStatus(st)} style={{
                          padding: '7px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                          border: '1px solid ' + (newStatus === st ? 'var(--ink)' : 'var(--border)'),
                          background: newStatus === st ? 'var(--ink)' : 'transparent',
                          color: newStatus === st ? 'var(--cream)' : 'var(--muted)',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                          {st}{st === 'Delivered' && selected.payment?.method === 'cod' ? ' (collect payment)' : ''}
                        </button>
                      ))}
                    </div>
                    <input className="input" placeholder="Internal note (optional)" value={note} onChange={e => setNote(e.target.value)} style={{ marginBottom: 12 }} />
                    <button onClick={handleStatusUpdate} disabled={updating || !newStatus}
                      style={{ width: '100%', padding: '11px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: newStatus ? 'var(--ink)' : 'var(--cream-dark)', color: newStatus ? 'var(--gold)' : 'var(--muted)', border: 'none', cursor: newStatus ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}>
                      {updating ? 'Updating...' : 'Confirm Update'}
                    </button>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--muted)', padding: '12px 0', fontStyle: 'italic' }}>No further transitions available.</div>
                )}
              </div>

              {/* History */}
              {selected.statusHistory?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <SectionTitle>History</SectionTitle>
                  <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {[...selected.statusHistory].reverse().map((h, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--cream-dark)', fontSize: 11 }}>
                        <StatusBadge status={h.status} />
                        <span style={{ flex: 1, color: 'var(--muted)' }}>{h.note}</span>
                        <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{h.changedAt ? format(new Date(h.changedAt), 'dd MMM, HH:mm') : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Note */}
              <AdminNoteEditor order={selected} onSaved={(updatedNote) => {
                setSelected(o => ({ ...o, adminNote: updatedNote }));
              }} />

            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── ADMIN NOTE EDITOR ─────────────────────────────────────────────────────────
function AdminNoteEditor({ order, onSaved }) {
  const [editing,  setEditing]  = useState(false);
  const [noteVal,  setNoteVal]  = useState(order.adminNote || '');
  const [saving,   setSaving]   = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateOrderStatus(order._id, { status: order.status, note: noteVal });
      onSaved(noteVal);
      setEditing(false);
      toast.success('Note saved');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save note'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <SectionTitle style={{ margin: 0 }}>Admin Note</SectionTitle>
        {!editing && (
          <button onClick={() => { setNoteVal(order.adminNote || ''); setEditing(true); }}
            style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border)', padding: '4px 10px', cursor: 'pointer', color: 'var(--muted)' }}>
            {order.adminNote ? 'Edit' : '+ Add Note'}
          </button>
        )}
      </div>

      {!editing ? (
        order.adminNote ? (
          <div style={{ background: '#fffde7', border: '1px solid #f9a825', padding: '10px 14px', fontSize: 12, color: '#5d4037', lineHeight: 1.7, fontStyle: 'italic' }}>
            {order.adminNote}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>No internal note yet.</div>
        )
      ) : (
        <div>
          <textarea
            value={noteVal}
            onChange={e => setNoteVal(e.target.value)}
            rows={3}
            placeholder="Internal note visible only to admins..."
            style={{ width: '100%', padding: '10px 12px', fontSize: 12, border: '1px solid var(--border)', fontFamily: "'Josefin Sans', sans-serif", resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={save} disabled={saving}
              style={{ flex: 1, padding: '8px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save Note'}
            </button>
            <button onClick={() => setEditing(false)}
              style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--muted)' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ADMIN DEPOSITS PAGE ───────────────────────────────────────────────────────
function AdminDepositsPage() {
  const [deposits, setDeposits]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [totalHeld, setTotalHeld] = useState(0);
  const [total, setTotal]         = useState(0);
  const [filter, setFilter]       = useState('held');
  const [selected, setSelected]   = useState(null);
  const [actionMode, setActionMode] = useState(null); // 'refund' | 'deduct' | 'mark-refunded' | 'mark-forfeited'
  const [refundForm, setRefundForm] = useState({ damageNote: '', damageDeduction: 0 });
  const [markNote, setMarkNote]   = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await depositAPI.getAll(params);
      setDeposits(data.deposits);
      setTotalHeld(data.totalHeld);
      setTotal(data.total);
    } catch { toast.error('Failed to load deposits'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchDeposits(); }, [fetchDeposits]);

  const openAction = (dep, mode) => {
    setSelected(dep);
    setActionMode(mode);
    setRefundForm({ damageNote: '', damageDeduction: 0 });
    setMarkNote('');
  };

  const handleQuickMark = async (action) => {
    setProcessing(true);
    try {
      await depositAPI.markDeposit(selected._id, { action, note: markNote });
      toast.success(action === 'refunded'
        ? ` Deposit of ₹${selected.heldAmount?.toLocaleString('en-IN')} marked as refunded`
        : `Deposit of ₹${selected.heldAmount?.toLocaleString('en-IN')} marked as forfeited`
      );
      setSelected(null);
      setActionMode(null);
      fetchDeposits();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleFullRefund = async () => {
    setProcessing(true);
    try {
      const deduction  = Number(refundForm.damageDeduction) || 0;
      const refundAmt  = selected.heldAmount - deduction;
      await depositAPI.processRefund(selected._id, {
        damageNote: refundForm.damageNote,
        damageDeduction: deduction,
      });
      toast.success(deduction > 0
        ? `₹${refundAmt} refunded, ₹${deduction} deducted for damage`
        : `Full deposit of ₹${refundAmt} refunded`
      );
      setSelected(null);
      setActionMode(null);
      fetchDeposits();
    } catch (e) { toast.error(e.response?.data?.message || 'Refund failed'); }
    finally { setProcessing(false); }
  };

  const statusColors = {
    held:               { bg: '#fff8e1', border: '#ffc107',  text: '#856404' },
    fully_refunded:     { bg: '#e8f5e9', border: '#4caf50',  text: '#1b5e20' },
    partially_refunded: { bg: '#e3f2fd', border: '#2196f3',  text: '#0d47a1' },
    forfeited:          { bg: '#ffebee', border: '#f44336',  text: '#b71c1c' },
  };

  const refundPreview = selected ? Math.max(0, selected.heldAmount - Number(refundForm.damageDeduction)) : 0;

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={s.pageTitle}>Deposit Management</h2>
          <p className="text-muted text-sm mt-4">{total} records · Security deposit tracking & refunds</p>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--gold)', padding: '16px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>Total Held</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, color: 'var(--gold)' }}>₹{totalHeld.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[['held', 'Held'], ['fully_refunded', 'Refunded'], ['partially_refunded', 'Partial'], ['forfeited', 'Forfeited'], ['', 'All']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{ ...s.filterChip, ...(filter === val ? s.filterChipActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <Spinner size="lg" centered /> : deposits.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--muted)' }}>
          
          <p>No {filter || ''} deposit records found.</p>
        </div>
      ) : (
        <div className="admin-scrolltable" style={{ background: 'white', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Order ID', 'Customer', 'Payment', 'Held Amount', 'Refunded', 'Deducted', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deposits.map(dep => {
                const sc = statusColors[dep.status] || statusColors.held;
                // Purchase orders always show ₹0 deposit
                const isPurchase = dep.order?.orderType === 'purchase';
                return (
                  <tr key={dep._id}>
                    <td style={s.td}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>#{dep.orderId}</span>
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 500, fontSize: 12 }}>{dep.user?.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>{dep.user?.email}</div>
                    </td>
                    <td style={s.td}>
                      {dep.order?.payment?.method === 'cod'
                        ? <span style={{ display:'inline-block', padding:'2px 8px', fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', background:'#fff3e0', border:'1px solid #ff9800', color:'#e65100' }}>COD</span>
                        : <span style={{ display:'inline-block', padding:'2px 8px', fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', background:'#e8f5e9', border:'1px solid #4caf50', color:'#1b5e20' }}>Online</span>
                      }
                    </td>
                    <td style={{ ...s.td, fontWeight: 700, color: isPurchase ? 'var(--muted)' : 'var(--gold)' }}>
                      {isPurchase ? '₹0' : `₹${dep.heldAmount?.toLocaleString('en-IN')}`}
                    </td>
                    <td style={{ ...s.td, color: '#1b5e20' }}>
                      {dep.refundedAmount > 0 ? `₹${dep.refundedAmount.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td style={{ ...s.td, color: dep.deductedAmount > 0 ? '#b71c1c' : 'var(--muted)' }}>
                      {dep.deductedAmount > 0 ? `₹${dep.deductedAmount.toLocaleString('en-IN')}` : '—'}
                      {dep.isDamaged && <div style={{ fontSize: 9, color: '#b71c1c', marginTop: 2 }}> Damaged</div>}
                    </td>
                    <td style={s.td}>
                      <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}>
                        {dep.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ ...s.td, fontSize: 11, color: 'var(--muted)' }}>
                      {dep.createdAt ? format(new Date(dep.createdAt), 'dd MMM yyyy') : '—'}
                    </td>
                    <td style={s.td}>
                      {isPurchase ? (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>N/A</span>
                      ) : dep.status === 'held' ? (
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          <button className="btn btn-sm" style={{ background: '#e8f5e9', border: '1px solid #4caf50', color: '#1b5e20', fontSize: 11 }}
                            onClick={() => openAction(dep, 'mark-refunded')}>
                            ✓ Refunded
                          </button>
                          <button className="btn btn-sm" style={{ background: '#ffebee', border: '1px solid #f44336', color: '#b71c1c', fontSize: 11 }}
                            onClick={() => openAction(dep, 'mark-forfeited')}>
                            ✕ Forfeit
                          </button>
                          <button className="btn btn-outline btn-sm"
                            onClick={() => openAction(dep, 'refund')}>
                            Partial
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {dep.refundedAt ? format(new Date(dep.refundedAt), 'dd MMM') : 'Done'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Action Modal ── */}
      {selected && actionMode && (
        <div className="modal-overlay" onClick={() => { setSelected(null); setActionMode(null); }}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setSelected(null); setActionMode(null); }}>✕</button>

            {(actionMode === 'mark-refunded' || actionMode === 'mark-forfeited') && (
              <>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, marginBottom: 4 }}>
                  {actionMode === 'mark-refunded' ? 'Mark as Refunded' : 'Forfeit Deposit'}
                </h2>
                <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                  Order #{selected.orderId} · {selected.user?.name} · ₹{selected.heldAmount?.toLocaleString('en-IN')}
                </p>
                {actionMode === 'mark-forfeited' && (
                  <div style={{ background: '#ffebee', border: '1px solid #f44336', padding: '12px 14px', marginBottom: 16, fontSize: 12 }}>
                    This will mark the full deposit of ₹{selected.heldAmount?.toLocaleString('en-IN')} as <strong>forfeited</strong>. This cannot be undone.
                  </div>
                )}
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label className="form-label">Note (optional)</label>
                  <input className="input" value={markNote} onChange={e => setMarkNote(e.target.value)}
                    placeholder={actionMode === 'mark-refunded' ? 'e.g. Refunded via bank transfer' : 'e.g. Item returned damaged'} />
                </div>
                <button
                  className="btn btn-gold w-full"
                  style={actionMode === 'mark-forfeited' ? { background: '#f44336', border: 'none', color: 'white' } : {}}
                  onClick={() => handleQuickMark(actionMode === 'mark-refunded' ? 'refunded' : 'forfeited')}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : actionMode === 'mark-refunded' ? 'Confirm Refunded' : 'Confirm Forfeit'}
                </button>
              </>
            )}

            {actionMode === 'refund' && (
              <>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, marginBottom: 4 }}>Process Refund</h2>
                <p className="text-muted text-sm" style={{ marginBottom: 14 }}>
                  Order #{selected.orderId} · {selected.user?.name}
                </p>

                {selected.order?.payment?.method === 'cod' ? (
                  <div style={{ background: '#fff3e0', border: '1px solid #ff9800', padding: '11px 14px', marginBottom: 16, fontSize: 12, color: '#e65100' }}>
                    <strong>⚠ COD Order</strong> — Razorpay refund is not applicable. You must transfer ₹{selected.heldAmount?.toLocaleString('en-IN')} manually (UPI / bank transfer) to the customer and then mark as refunded below.
                  </div>
                ) : (
                  <div style={{ background: '#e8f5e9', border: '1px solid #4caf50', padding: '11px 14px', marginBottom: 16, fontSize: 12, color: '#1b5e20' }}>
                    <strong>✓ Online Payment</strong> — Razorpay will automatically refund to the customer's original payment method.
                  </div>
                )}

                <div style={{ background: 'var(--cream)', padding: 14, marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="text-muted text-sm">Total Held</span>
                    <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--gold)' }}>₹{selected.heldAmount?.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Damage Deduction (₹)</label>
                  <input className="input" type="number" min={0} max={selected.heldAmount}
                    value={refundForm.damageDeduction}
                    onChange={e => setRefundForm(f => ({ ...f, damageDeduction: e.target.value }))}
                    placeholder="0 = no damage" />
                  <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Enter 0 for full refund</p>
                </div>

                {Number(refundForm.damageDeduction) > 0 && (
                  <div className="form-group">
                    <label className="form-label">Damage Note *</label>
                    <textarea className="input" rows={2}
                      value={refundForm.damageNote}
                      onChange={e => setRefundForm(f => ({ ...f, damageNote: e.target.value }))}
                      placeholder="Describe the damage…" />
                  </div>
                )}

                <div style={{ border: '1px solid var(--border)', padding: 14, marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span className="text-muted text-sm">Held</span>
                    <span>₹{selected.heldAmount?.toLocaleString('en-IN')}</span>
                  </div>
                  {Number(refundForm.damageDeduction) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#b71c1c', fontSize: 13 }}>Deduction</span>
                      <span style={{ color: '#b71c1c' }}>− ₹{Number(refundForm.damageDeduction).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <hr className="divider" />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700 }}>Refund Amount</span>
                    <span style={{ fontWeight: 700, fontSize: 18, color: refundPreview > 0 ? '#1b5e20' : '#b71c1c' }}>
                      ₹{refundPreview.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                <button
                  className="btn btn-gold btn-lg w-full"
                  onClick={handleFullRefund}
                  disabled={processing || (Number(refundForm.damageDeduction) > 0 && !refundForm.damageNote)}
                >
                  {processing ? 'Processing...' : `Refund ₹${refundPreview.toLocaleString('en-IN')}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ADMIN PRODUCTS ────────────────────────────────────────────────────────────
const BLANK_FORM = {
  name: '', description: '', category: '', brand: '', material: '', careInstructions: '',
  sizes: [],
  stockBySize: { XS: 0, S: 0, M: 0, L: 0, XL: 0, XXL: 0, XXXL: 0, '26': 0, '28': 0, '30': 0, '32': 0, '34': 0, '36': 0, '38': 0, '40': 0 },
  rentalPrices: { twoDays: '', fiveDays: '', oneWeek: '', twoWeeks: '' },
  securityDeposit: '',
  images: [{ url: '', isPrimary: true }],
  tags: '',
  isActive: true,
  isPurchasable: false,
  purchasePrice: '',
};

const ALL_SIZES  = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const BOTTOM_SIZES = ['26', '28', '30', '32', '34', '36', '38', '40'];
const BOTTOM_CATEGORIES = ['Bottom Wear', 'Trousers', 'Jeans', 'Palazzos', 'Leggings', 'Shorts', 'Skirts'];
const CATEGORIES = ['Formal', 'Casual', 'Party', 'Traditional', 'Streetwear', 'Luxury Evening', 'Ethnic', 'Western', 'Fusion', 'Bottom Wear', 'Trousers', 'Jeans', 'Palazzos', 'Leggings', 'Shorts', 'Skirts'];

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [confirm,  setConfirm]  = useState(null);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(BLANK_FORM);
  const [saving,          setSaving]          = useState(false);
  const [prodSearch,      setProdSearch]      = useState('');
  const [prodFilter,      setProdFilter]      = useState('all');
  const [bulkDepositOpen,   setBulkDepositOpen]   = useState(false);
  const [bulkDepositFilter, setBulkDepositFilter] = useState('all');
  const [bulkDepositMethod, setBulkDepositMethod] = useState('fixed');
  const [bulkDepositValue,  setBulkDepositValue]  = useState('');
  const [bulkDepositSaving, setBulkDepositSaving] = useState(false);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { default: api } = await import('../services/api');
      const { data } = await api.get('/products', { params: { limit: 100, isActive: 'all' } });
      setProducts(data.products);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const handleBulkDeposit = async () => {
    if (!bulkDepositValue || Number(bulkDepositValue) <= 0) { toast.error('Enter a valid amount'); return; }
    const targets = bulkDepositFilter === 'all' ? products : products.filter(p => p.category === bulkDepositFilter);
    if (!window.confirm(`Update security deposit for ${targets.length} products?`)) return;
    setBulkDepositSaving(true);
    let ok = 0, fail = 0;
    for (const p of targets) {
      try {
        const deposit = bulkDepositMethod === 'fixed'
          ? Number(bulkDepositValue)
          : Math.round((p.rentalPrices?.twoDays || 0) * Number(bulkDepositValue) / 100);
        await adminAPI.updateProduct(p._id, { ...p, securityDeposit: deposit });
        ok++;
      } catch { fail++; }
    }
    setBulkDepositSaving(false);
    setBulkDepositOpen(false);
    setBulkDepositValue('');
    toast.success(`Updated ${ok} products${fail > 0 ? `, ${fail} failed` : ''}`);
    fetchProducts();
  };

  const openAdd  = () => { setForm(BLANK_FORM); setModal('add'); };
  const openEdit = (product) => {
    setForm({
      name: product.name || '', description: product.description || '',
      category: product.category || '', brand: product.brand || '',
      material: product.material || '', careInstructions: product.careInstructions || '',
      sizes: product.sizes || [],
      stockBySize: { XS: 0, S: 0, M: 0, L: 0, XL: 0, XXL: 0, XXXL: 0, '26': 0, '28': 0, '30': 0, '32': 0, '34': 0, '36': 0, '38': 0, '40': 0, ...(product.stockBySize || {}) },
      rentalPrices: {
        twoDays: product.rentalPrices?.twoDays || '', fiveDays: product.rentalPrices?.fiveDays || '',
        oneWeek: product.rentalPrices?.oneWeek || '', twoWeeks: product.rentalPrices?.twoWeeks || '',
      },
      securityDeposit: product.securityDeposit ?? '',
      images: product.images?.length ? product.images.map(img => ({ url: img.url || '', isPrimary: img.isPrimary || false })) : [{ url: '', isPrimary: true }],
      tags: (product.tags || []).join(', '),
      isActive: product.isActive !== false,
      isPurchasable: product.isPurchasable || false,
      purchasePrice: product.purchasePrice ?? '',
      _id: product._id,
    });
    setModal('edit');
  };

  const handleToggle = async (id) => {
    try { const { data } = await adminAPI.toggleProduct(id); toast.success(data.message); fetchProducts(); }
    catch { toast.error('Toggle failed'); }
  };
  const handleDelete = async (id) => {
    try { await adminAPI.deleteProduct(id); toast.success('Product deleted'); fetchProducts(); }
    catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
    finally { setConfirm(null); }
  };

  const handleSave = async () => {
    if (!form.name.trim())           { toast.error('Product name is required.');        return; }
    if (!form.description.trim())    { toast.error('Description is required.');         return; }
    if (!form.category)              { toast.error('Please select a category.');        return; }
    if (form.sizes.length === 0)     { toast.error('Select at least one size.');        return; }
    if (!form.rentalPrices.twoDays)  { toast.error('2-day rental price is required.');  return; }
    if (!form.rentalPrices.fiveDays) { toast.error('5-day rental price is required.');  return; }
    if (!form.rentalPrices.oneWeek)  { toast.error('1-week rental price is required.'); return; }
    if (!form.rentalPrices.twoWeeks) { toast.error('2-week rental price is required.'); return; }
    if (form.securityDeposit === '') { toast.error('Security deposit is required.');    return; }
    const imageUrls = form.images.filter(img => img.url.trim());
    if (imageUrls.length === 0)      { toast.error('Add at least one image URL.');      return; }

    const payload = {
      name: form.name.trim(), description: form.description.trim(),
      category: form.category, brand: form.brand.trim(),
      material: form.material.trim(), careInstructions: form.careInstructions.trim(),
      sizes: form.sizes, stockBySize: form.stockBySize,
      rentalPrices: {
        twoDays: Number(form.rentalPrices.twoDays), fiveDays: Number(form.rentalPrices.fiveDays),
        oneWeek: Number(form.rentalPrices.oneWeek), twoWeeks: Number(form.rentalPrices.twoWeeks),
      },
      securityDeposit: Number(form.securityDeposit),
      images: imageUrls.map((img, i) => ({ url: img.url.trim(), isPrimary: i === 0 })),
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      isActive: form.isActive,
      isPurchasable: form.isPurchasable,
      purchasePrice: form.isPurchasable && form.purchasePrice !== '' ? Number(form.purchasePrice) : null,
    };

    setSaving(true);
    try {
      if (modal === 'add') { await adminAPI.createProduct(payload); toast.success('Product created!'); }
      else { await adminAPI.updateProduct(form._id, payload); toast.success('Product updated!'); }
      setModal(null);
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed. Check all fields.'); }
    finally { setSaving(false); }
  };

  const toggleSize = (size) => setForm(f => ({ ...f, sizes: f.sizes.includes(size) ? f.sizes.filter(s => s !== size) : [...f.sizes, size] }));
  const updateImageUrl = (idx, url) => setForm(f => { const images = [...f.images]; images[idx] = { ...images[idx], url }; return { ...f, images }; });
  const addImageRow    = () => setForm(f => ({ ...f, images: [...f.images, { url: '', isPrimary: false }] }));
  const removeImageRow = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  if (loading) return <Spinner size="lg" centered />;

  const filteredProducts = products.filter(p => {
    const matchSearch = !prodSearch || p.name.toLowerCase().includes(prodSearch.toLowerCase()) || p.category?.toLowerCase().includes(prodSearch.toLowerCase()) || p.brand?.toLowerCase().includes(prodSearch.toLowerCase());
    const matchFilter = prodFilter === 'all' ? true
      : prodFilter === 'active'   ? p.isActive
      : prodFilter === 'inactive' ? !p.isActive
      : prodFilter === 'lowstock' ? Object.values(p.stockBySize || {}).some(v => v <= 2)
      : true;
    return matchSearch && matchFilter;
  });

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={s.pageTitle}>Products</h2>
          <p className="text-muted text-sm mt-4">{filteredProducts.length} of {products.length} items shown</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setBulkDepositOpen(true)}
            style={{ padding:'9px 16px', fontSize:10, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', background:'white', color:'var(--ink)', border:'1px solid var(--border)', cursor:'pointer' }}>
            ₹ Bulk Deposit
          </button>
          <button className="btn btn-gold" onClick={openAdd}>+ Add Product</button>
        </div>
      </div>

      {/* ── Bulk Deposit Modal ── */}
      {bulkDepositOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'white', width:'100%', maxWidth:440, padding:28 }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, marginBottom:6 }}>Bulk Update Security Deposit</div>
            <p style={{ fontSize:12, color:'var(--muted)', marginBottom:20, lineHeight:1.7 }}>
              Update the security deposit for multiple products at once. You can set a fixed amount or a percentage of the 2-day rental price.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', display:'block', marginBottom:6 }}>Apply To</label>
                <select className="input" value={bulkDepositFilter} onChange={e => setBulkDepositFilter(e.target.value)}>
                  <option value="all">All Products ({products.length})</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c} ({products.filter(p=>p.category===c).length})</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', display:'block', marginBottom:6 }}>Method</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[['fixed','Fixed Amount (₹)'],['percent','% of 2-day price']].map(([v,l]) => (
                    <button key={v} onClick={() => setBulkDepositMethod(v)}
                      style={{ flex:1, padding:'8px 0', fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', border:'1px solid var(--border)', cursor:'pointer', background: bulkDepositMethod===v ? 'var(--ink)' : 'white', color: bulkDepositMethod===v ? 'var(--cream)' : 'var(--ink)' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', display:'block', marginBottom:6 }}>
                  {bulkDepositMethod === 'fixed' ? 'Amount (₹)' : 'Percentage (%)'}
                </label>
                <input className="input" type="number" min="0" max={bulkDepositMethod==='percent'?100:undefined}
                  value={bulkDepositValue} onChange={e => setBulkDepositValue(e.target.value)}
                  placeholder={bulkDepositMethod === 'fixed' ? 'e.g. 500' : 'e.g. 30'} />
                {bulkDepositMethod === 'percent' && bulkDepositValue && (
                  <div style={{ fontSize:11, color:'var(--muted)', marginTop:6 }}>
                    Example: ₹500 rental → ₹{Math.round(500 * Number(bulkDepositValue) / 100)} deposit
                  </div>
                )}
              </div>
              <div style={{ background:'var(--cream)', border:'1px solid var(--border)', padding:'10px 14px', fontSize:11, color:'var(--muted)' }}>
                This will update <strong style={{ color:'var(--ink)' }}>
                  {bulkDepositFilter === 'all' ? products.length : products.filter(p=>p.category===bulkDepositFilter).length}
                </strong> products. This cannot be undone.
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:24, justifyContent:'flex-end' }}>
              <button onClick={() => setBulkDepositOpen(false)} className="btn btn-ghost">Cancel</button>
              <button onClick={handleBulkDeposit} disabled={bulkDepositSaving || !bulkDepositValue}
                style={{ padding:'10px 24px', fontSize:10, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', background:'var(--ink)', color:'var(--gold)', border:'none', cursor: bulkDepositValue ? 'pointer' : 'not-allowed', opacity: bulkDepositValue ? 1 : 0.5 }}>
                {bulkDepositSaving ? 'Updating...' : 'Update All'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        <input
          placeholder="Search products..."
          value={prodSearch} onChange={e => setProdSearch(e.target.value)}
          style={{ flex:1, minWidth:180, padding:'8px 12px', border:'1px solid var(--border)', fontSize:12, background:'white', outline:'none' }}
        />
        <select value={prodFilter} onChange={e => setProdFilter(e.target.value)}
          style={{ padding:'8px 12px', border:'1px solid var(--border)', fontSize:11, background:'white', cursor:'pointer' }}>
          <option value="all">All Products</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
          <option value="lowstock">Low Stock (≤2)</option>
        </select>
      </div>

      <div className="admin-scrolltable" style={{ background: 'white', border: '1px solid var(--border)', overflowX: 'auto' }}>
        {products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--muted)' }}>
            
            <div style={{ fontWeight: 600, marginBottom: 8 }}>No products yet</div>
            <button className="btn btn-gold btn-sm" onClick={openAdd}>Add Your First Product</button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Product', 'Category', 'Sizes', '2-Day', '1-Week', 'Deposit', 'Rentals', 'Status', 'Actions'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filteredProducts.map(p => (
                <tr key={p._id}>
                  <td style={s.td}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      {p.images?.[0]?.url
                        ? <img src={p.images[0].url} alt={p.name} style={{ width:36, height:44, objectFit:'cover', border:'1px solid var(--border)' }} />
                        : <div style={{ width:36, height:44, background:'var(--cream)', border:'1px solid var(--border)' }} />
                      }
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{p.name}</div>
                        {p.brand && <div style={{ fontSize:10, color:'var(--muted)' }}>{p.brand}</div>}
                        {Object.values(p.stockBySize||{}).some(v=>v<=2) && (
                          <div style={{ fontSize:9, fontWeight:700, color:'#e65100', marginTop:2, letterSpacing:'0.08em' }}>⚠ LOW STOCK</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={s.td}><span className="tag">{p.category}</span></td>
                  <td style={s.td}><span style={{ fontSize: 11 }}>{p.sizes?.join(', ') || '—'}</span></td>
                  <td style={s.td}><strong>₹{p.rentalPrices?.twoDays}</strong></td>
                  <td style={s.td}>₹{p.rentalPrices?.oneWeek}</td>
                  <td style={s.td}>₹{p.securityDeposit}</td>
                  <td style={s.td}>{p.totalRentals || 0}</td>
                  <td style={s.td}>
                    <span style={p.isActive ? sp.activeBadge : sp.inactiveBadge}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {p.isPurchasable && (
                      <span style={{ display: 'block', marginTop: 4, fontSize: 9, fontWeight: 700, color: '#4a148c', border: '1px solid #9c27b0', padding: '1px 6px', background: '#f3e5f5' }}>
                         PURCHASABLE
                      </span>
                    )}
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleToggle(p._id)}>
                        {p.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#c0392b' }} onClick={() => setConfirm({ id: p._id, name: p.name })}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div style={sp.overlay} onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="admin-drawer" style={sp.drawer}>
            <div style={sp.drawerHead}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26 }}>
                {modal === 'add' ? '+ Add New Product' : `Edit: ${form.name || 'Product'}`}
              </h3>
              <button onClick={() => setModal(null)} style={sp.closeBtn}>✕</button>
            </div>
            <div style={sp.drawerBody}>
              <SectionTitle>Basic Information</SectionTitle>
              <FormRow label="Product Name *">
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Silk Embroidered Lehenga" />
              </FormRow>
              <FormRow label="Description *">
                <textarea className="input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ resize: 'vertical', minHeight: 80 }} />
              </FormRow>
              <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <FormRow label="Category *">
                  <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormRow>
                <FormRow label="Brand">
                  <input className="input" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="e.g. Sabyasachi" />
                </FormRow>
              </div>
              <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <FormRow label="Material">
                  <input className="input" value={form.material} onChange={e => setForm(f => ({ ...f, material: e.target.value }))} placeholder="e.g. Pure Silk" />
                </FormRow>
                <FormRow label="Tags (comma separated)">
                  <input className="input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="wedding, bridal" />
                </FormRow>
              </div>
              <FormRow label="Care Instructions">
                <input className="input" value={form.careInstructions} onChange={e => setForm(f => ({ ...f, careInstructions: e.target.value }))} placeholder="e.g. Dry clean only" />
              </FormRow>

              <SectionTitle>Sizes Available *</SectionTitle>
              {(() => {
                const isBottomWear = BOTTOM_CATEGORIES.includes(form.category);
                const sizeSet = isBottomWear ? BOTTOM_SIZES : ALL_SIZES;
                return (
                  <>
                    {isBottomWear && (
                      <div style={{ fontSize: 11, color: 'var(--gold)', background: 'rgba(184,146,74,0.08)', border: '1px solid rgba(184,146,74,0.2)', padding: '6px 12px', marginBottom: 8 }}>
                        Bottom wear — showing waist sizes (inches)
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      {sizeSet.map(size => (
                        <button key={size} type="button" onClick={() => toggleSize(size)}
                          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, border: `2px solid ${form.sizes.includes(size) ? 'var(--gold)' : 'var(--border)'}`, background: form.sizes.includes(size) ? 'rgba(184,146,74,0.12)' : 'white', color: form.sizes.includes(size) ? 'var(--gold)' : 'var(--muted)', cursor: 'pointer' }}>
                          {size}
                        </button>
                      ))}
                    </div>
                  </>
                );
              })()}
              {form.sizes.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, marginTop: 12 }}>Stock per Size</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {form.sizes.map(size => (
                      <div key={size} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>{size}</label>
                        <input type="number" min={0} className="input" style={{ width: 64, textAlign: 'center' }}
                          value={form.stockBySize[size] || 0}
                          onChange={e => setForm(f => ({ ...f, stockBySize: { ...f.stockBySize, [size]: Number(e.target.value) } }))} />
                      </div>
                    ))}
                  </div>
                </>
              )}

              <SectionTitle>Rental Prices (₹) *</SectionTitle>
              <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[{ key: 'twoDays', label: '2 Days *' }, { key: 'fiveDays', label: '5 Days *' }, { key: 'oneWeek', label: '1 Week *' }, { key: 'twoWeeks', label: '2 Weeks *' }].map(({ key, label }) => (
                  <FormRow key={key} label={label}>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 13 }}>₹</span>
                      <input className="input" type="number" min={0} style={{ paddingLeft: 28 }}
                        value={form.rentalPrices[key]} onChange={e => setForm(f => ({ ...f, rentalPrices: { ...f.rentalPrices, [key]: e.target.value } }))} placeholder="0" />
                    </div>
                  </FormRow>
                ))}
              </div>
              <FormRow label="Security Deposit (₹) *">
                <div style={{ position: 'relative', maxWidth: 200 }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 13 }}>₹</span>
                  <input className="input" type="number" min={0} style={{ paddingLeft: 28 }}
                    value={form.securityDeposit} onChange={e => setForm(f => ({ ...f, securityDeposit: e.target.value }))} placeholder="0" />
                </div>
              </FormRow>

              <SectionTitle>Product Images *</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.images.map((img, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: idx === 0 ? 'var(--gold)' : 'var(--muted)', minWidth: 50 }}>
                      {idx === 0 ? 'Primary' : `Image ${idx + 1}`}
                    </div>
                    <input className="input" style={{ flex: 1 }} value={img.url} onChange={e => updateImageUrl(idx, e.target.value)} placeholder="https://..." />
                    {img.url && <img src={img.url} alt="" style={{ width: 36, height: 44, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />}
                    {form.images.length > 1 && <button type="button" onClick={() => removeImageRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontSize: 18 }}>✕</button>}
                  </div>
                ))}
                <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={addImageRow}>+ Add Another Image</button>
              </div>

              <SectionTitle>Visibility</SectionTitle>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} style={{ width: 16, height: 16, accentColor: 'var(--gold)' }} />
                <span style={{ fontSize: 13 }}>Active — visible to customers</span>
              </label>

              <SectionTitle>Buy After Rental</SectionTitle>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
                <input type="checkbox" checked={form.isPurchasable} onChange={e => setForm(f => ({ ...f, isPurchasable: e.target.checked }))} style={{ width: 16, height: 16, accentColor: 'var(--gold)' }} />
                <span style={{ fontSize: 13 }}>Allow customers to purchase after completing a rental</span>
              </label>
              {form.isPurchasable && (
                <FormRow label="Purchase Price (₹) *">
                  <div style={{ position: 'relative', maxWidth: 200 }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 13 }}>₹</span>
                    <input className="input" type="number" min={0} style={{ paddingLeft: 28 }}
                      value={form.purchasePrice} onChange={e => setForm(f => ({ ...f, purchasePrice: e.target.value }))} placeholder="0" />
                  </div>
                </FormRow>
              )}
            </div>
            <div style={sp.drawerFoot}>
              <button className="btn btn-outline" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSave} disabled={saving} style={{ minWidth: 160 }}>
                {saving ? 'Saving...' : modal === 'add' ? 'Create Product' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog isOpen={!!confirm} title="Delete Product"
        message={`Delete "${confirm?.name}"? This cannot be undone.`}
        onConfirm={() => handleDelete(confirm.id)} onCancel={() => setConfirm(null)}
        confirmLabel="Delete" danger />
    </div>
  );
}

// ── ADMIN USERS ───────────────────────────────────────────────────────────────
function AdminUsers() {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail,   setDetail]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search,        setSearch]        = useState('');
  const [collegeFilter, setCollegeFilter]  = useState('all'); // 'all' | 'college' | 'non-college'

  useEffect(() => {
    adminAPI.getUsers()
      .then(({ data }) => setUsers(data.users))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openDetail = async (userId) => {
    setSelected(userId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await adminAPI.getUserDetail(userId);
      setDetail(data);
    } catch { toast.error('Failed to load user details'); }
    finally { setDetailLoading(false); }
  };

  const closeDetail = () => { setSelected(null); setDetail(null); };

  const toggleUser = async (id, isActive) => {
    try {
      await adminAPI.toggleUser(id);
      toast.success(isActive ? 'User suspended' : 'User activated');
      setUsers(u => u.map(user => user._id === id ? { ...user, isActive: !user.isActive } : user));
      if (detail?.user?._id === id) {
        setDetail(d => ({ ...d, user: { ...d.user, isActive: !d.user.isActive } }));
      }
    } catch { toast.error('Action failed'); }
  };

  const idColors = {
    approved:      { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20' },
    pending:       { bg: '#fff8e1', border: '#ffc107', text: '#856404' },
    rejected:      { bg: '#ffebee', border: '#f44336', text: '#b71c1c' },
    not_submitted: { bg: '#fafafa', border: '#9e9e9e', text: '#424242' },
  };

  const filtered = users.filter(u => {
    const matchesSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search);
    const matchesCollege =
      collegeFilter === 'all' ? true :
      collegeFilter === 'college' ? !!u.isCollegeVerified :
      !u.isCollegeVerified;
    return matchesSearch && matchesCollege;
  });

  if (loading) return <Spinner size="lg" centered />;

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={s.pageTitle}>Users</h2>
          <p className="text-muted text-sm mt-4">{filtered.length} of {users.length} customers</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { val: 'all',         label: `All (${users.length})` },
            { val: 'college',     label: `College (${users.filter(u => u.isCollegeVerified).length})` },
            { val: 'non-college', label: `Non-College (${users.filter(u => !u.isCollegeVerified).length})` },
          ].map(({ val, label }) => (
            <button key={val} onClick={() => setCollegeFilter(val)}
              style={{ padding: '6px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', border: '1px solid var(--border)', cursor: 'pointer', background: collegeFilter === val ? 'var(--ink)' : 'white', color: collegeFilter === val ? 'var(--cream)' : 'var(--ink)' }}>
              {label}
            </button>
          ))}
          <input className="input" placeholder="Search by name, email or phone..." value={search}
            onChange={e => setSearch(e.target.value)} style={{ width: 'clamp(160px, 35vw, 240px)', fontSize: 13 }} />
        </div>
      </div>

      <div className="admin-scrolltable" style={{ background: 'white', border: '1px solid var(--border)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Name', 'Email', 'College', 'Email ✓', 'Warba Score', 'Joined', 'Status', 'Actions'].map(h => (
              <th key={h} style={s.th}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map(user => {
              return (
                <tr key={user._id} style={{ cursor: 'pointer' }} onClick={() => openDetail(user._id)}>
                  <td style={s.td}><div style={{ fontWeight: 600, fontSize: 13 }}>{user.name}</div><div style={{ fontSize: 10, color: 'var(--muted)' }}>{user.college || '—'}</div></td>
                  <td style={s.td}><span style={{ fontSize: 12 }}>{user.email}</span></td>
                  <td style={s.td}><span style={{ fontSize: 12 }}>{user.phone ? `+91${user.phone}` : '—'}</span></td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{user.isEmailVerified ? '✓' : '—'}</td>
                  <td style={s.td}>
                    {user.isCollegeVerified
                      ? <span style={{ display: 'inline-block', padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#e8f5e9', border: '1px solid #4caf50', color: '#1b5e20' }}>{user.college || 'Verified'}</span>
                      : <span style={{ fontSize: 11, color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td style={{ ...s.td, fontSize: 11, color: 'var(--muted)' }}>{user.createdAt ? format(new Date(user.createdAt), 'dd MMM yyyy') : '—'}</td>
                  <td style={s.td}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: user.isActive ? '#e8f5e9' : '#ffebee', border: user.isActive ? '1px solid #4caf50' : '1px solid #f44336', color: user.isActive ? '#1b5e20' : '#b71c1c' }}>
                      {user.isActive ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td style={s.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openDetail(user._id)}>View</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => toggleUser(user._id, user.isActive)}>
                        {user.isActive ? 'Suspend' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div style={sp.overlay} onClick={e => e.target === e.currentTarget && closeDetail()}>
          <div className="admin-drawer" style={{ ...sp.drawer, width: 'min(720px, 95vw)' }}>
            <div style={sp.drawerHead}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24 }}>
                {detail?.user?.name || 'Loading...'}
              </h3>
              <button onClick={closeDetail} style={sp.closeBtn}>✕</button>
            </div>
            <div style={{ ...sp.drawerBody, padding: 0 }}>
              {detailLoading ? (
                <div style={{ padding: 40 }}><Spinner size="lg" centered /></div>
              ) : detail ? (
                <UserDetailContent detail={detail} onToggle={toggleUser} idColors={idColors} />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserDetailContent({ detail, onToggle, idColors }) {
  const { user, orders, deposits, totalSpent } = detail;
  const [tab, setTab] = useState('overview');
  const [trust, setTrust] = useState(null);

  useEffect(() => {
    import('../services/api').then(({ default: api }) => {
      api.get(`/trust/admin/${user._id}`)
        .then(r => setTrust(r.data.trust))
        .catch(() => {});
    });
  }, [user._id]);

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'orders',   label: `Orders (${orders.length})` },
    { key: 'deposits', label: `Deposits (${deposits.length})` },
    { key: 'trust',    label: 'Warba Score' },
  ];

  return (
    <div>
      <div style={{ padding: '20px 24px', background: 'var(--cream)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: 'white', flexShrink: 0 }}>
            {user.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 18, fontFamily: "'Cormorant Garamond', serif" }}>{user.name}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{user.email}</div>
            {user.phone && <div style={{ fontSize: 12, color: 'var(--muted)' }}>+91{user.phone}</div>}
            {user.college && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{user.college}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {user.isCollegeVerified && (
              <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#e8f5e9', border: '1px solid #4caf50', color: '#1b5e20' }}>
                College ✓
              </span>
            )}
            <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: user.isActive ? '#e8f5e9' : '#ffebee', border: user.isActive ? '1px solid #4caf50' : '1px solid #f44336', color: user.isActive ? '#1b5e20' : '#b71c1c' }}>
              {user.isActive ? 'Active' : 'Suspended'}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => onToggle(user._id, user.isActive)}>
              {user.isActive ? 'Suspend' : 'Activate'}
            </button>
            {user.phone && (
              <a href={`https://wa.me/91${user.phone.replace(/[^0-9]/g,'')}?text=Hi%20${encodeURIComponent(user.name?.split(' ')[0]||'')}%2C%20this%20is%20Warbadobe%20team.`}
                target="_blank" rel="noreferrer"
                style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 12px', fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#25D366', border:'1px solid #25D366', textDecoration:'none' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Orders',   value: orders.length },
            { label: 'Total Spent',    value: `₹${totalSpent.toLocaleString('en-IN')}` },
            { label: 'Active Rentals', value: orders.filter(o => o.status === 'Active Rental').length },
            { label: 'Joined',         value: user.createdAt ? format(new Date(user.createdAt), 'dd MMM yyyy') : '—' },
            { label: 'Email',          value: user.isEmailVerified ? 'Verified' : 'Not verified' },
          ].map(stat => (
            <div key={stat.label} style={{ fontSize: 11 }}>
              <div style={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 9, fontWeight: 700, marginBottom: 3 }}>{stat.label}</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{stat.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'white' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '12px 18px', fontSize: 12, fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? 'var(--gold)' : 'var(--muted)', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid var(--gold)' : '2px solid transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '20px 24px' }}>
        {tab === 'overview' && (
          <>
          {/* Custom WhatsApp message */}
          {user.phone && (
            <WhatsAppMessageSender phone={user.phone} name={user.name} />
          )}
          <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 12 }}>Account Info</div>
              {[
                ['Name',     user.name],
                ['Email',    user.email],
                ['Phone',    user.phone ? `+91${user.phone}` : '—'],
                ['College',  user.college || '—'],
                ['Role',     user.role],
                ['Referral', user.referralCode || '—'],
                ['Referrals given', user.referralCount || 0],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--muted)' }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 12 }}>Recent Orders</div>
              {orders.slice(0, 5).map(o => (
                <div key={o._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>#{o.orderId}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>₹{o.grandTotal?.toLocaleString('en-IN')}</div>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
              ))}
              {orders.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)', padding: '16px 0' }}>No orders yet.</div>}
            </div>
          </div>
          </>
        )}

        {tab === 'orders' && (
          orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>No orders yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{['Order ID', 'Items', 'Total', 'Payment', 'Status', 'Date'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o._id}>
                      <td style={s.td}><span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>#{o.orderId}</span></td>
                      <td style={s.td}><span style={{ fontSize: 12 }}>{o.items?.length} item{o.items?.length !== 1 ? 's' : ''}</span></td>
                      <td style={s.td}><strong>₹{o.grandTotal?.toLocaleString('en-IN')}</strong></td>
                      <td style={s.td}><PayMethodBadge method={o.payment?.method} /></td>
                      <td style={s.td}><StatusBadge status={o.status} /></td>
                      <td style={{ ...s.td, fontSize: 11, color: 'var(--muted)' }}>{o.createdAt ? format(new Date(o.createdAt), 'dd MMM yyyy') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'deposits' && (
          deposits.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>No deposits on record.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{['Order ID', 'Held', 'Refunded', 'Deducted', 'Status', 'Date'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {deposits.map(d => (
                    <tr key={d._id}>
                      <td style={s.td}><span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>#{d.orderId}</span></td>
                      <td style={s.td}><strong>₹{d.heldAmount?.toLocaleString('en-IN')}</strong></td>
                      <td style={s.td}><span style={{ color: '#1b5e20', fontWeight: 600 }}>₹{(d.refundedAmount || 0).toLocaleString('en-IN')}</span></td>
                      <td style={s.td}><span style={{ color: d.deductedAmount > 0 ? '#b71c1c' : 'var(--muted)' }}>₹{(d.deductedAmount || 0).toLocaleString('en-IN')}</span></td>
                      <td style={s.td}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                          background: d.status === 'fully_refunded' ? '#e8f5e9' : d.status === 'partially_refunded' ? '#fff8e1' : '#e3f2fd',
                          border: `1px solid ${d.status === 'fully_refunded' ? '#4caf50' : d.status === 'partially_refunded' ? '#ffc107' : '#90caf9'}`,
                          color: d.status === 'fully_refunded' ? '#1b5e20' : d.status === 'partially_refunded' ? '#856404' : '#0d47a1'
                        }}>
                          {d.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ ...s.td, fontSize: 11, color: 'var(--muted)' }}>{d.createdAt ? format(new Date(d.createdAt), 'dd MMM yyyy') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'trust' && (
          <div style={{ padding: 20 }}>
            {!trust ? (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No trust score yet — user has not placed any orders.</div>
            ) : (
              <>
                {/* Stats grid */}
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
                  {[
                    ['Points',    trust.points,              'var(--gold)'],
                    ['Level',     trust.level?.name || '—',  '#5e35b1'],
                    ['Rentals',   trust.totalRentals,         'var(--ink)'],
                    ['Returns',   trust.successfulReturns,    '#2d6a2d'],
                    ['Late',      trust.lateReturns,          trust.lateReturns > 0 ? '#c45000' : 'var(--muted)'],
                    ['Cancelled', trust.cancelledOrders,      'var(--muted)'],
                  ].map(([k, v, col]) => (
                    <div key={k} style={{ minWidth: 70 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{k}</div>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, color: col, lineHeight: 1 }}>{v ?? '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Order limit */}
                {(() => {
                  const LIMITS = [
                    { name: 'New', min: 0, max: 99, limit: 399 },
                    { name: 'Trusted', min: 100, max: 299, limit: 999 },
                    { name: 'Silver', min: 300, max: 599, limit: 2499 },
                    { name: 'Gold', min: 600, max: 999, limit: 4999 },
                    { name: 'Elite', min: 1000, max: Infinity, limit: Infinity },
                  ];
                  const lv = LIMITS.find(l => trust.points >= l.min && trust.points <= l.max) || LIMITS[0];
                  return (
                    <div style={{ padding: '10px 14px', background: 'var(--cream)', border: '1px solid var(--border)', fontSize: 12, marginBottom: 20 }}>
                      Current order limit: <strong>₹{lv.limit === Infinity ? 'Unlimited' : lv.limit.toLocaleString('en-IN')}</strong> ({lv.name} level)
                      {user.isCollegeVerified && <span style={{ marginLeft: 8, color: '#2d6a2d', fontWeight: 700 }}>· College verified — unlimited</span>}
                    </div>
                  );
                })()}

                {/* Point history */}
                {trust.history?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Point History</div>
                    <div style={{ border: '1px solid var(--border)', maxHeight: 200, overflowY: 'auto' }}>
                      {trust.history.slice(0, 20).map((h, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--cream-dark)', fontSize: 12 }}>
                          <span style={{ color: 'var(--ink)' }}>{h.note || h.event}</span>
                          <span style={{ fontWeight: 700, color: h.points > 0 ? '#2d6a2d' : '#b71c1c', flexShrink: 0, marginLeft: 12 }}>{h.points > 0 ? '+' : ''}{h.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin actions */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={async () => {
                    const reason = window.prompt('Enter ban reason:');
                    if (!reason) return;
                    try { await trustAPI.banUser({ userId: user._id, reason }); toast.success('User banned'); }
                    catch(e) { toast.error(e.response?.data?.message || 'Failed to ban user'); }
                  }} style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#b71c1c', color: 'white', border: 'none', cursor: 'pointer' }}>
                    Ban User
                  </button>
                  {trust.isBanned && (
                    <button onClick={async () => {
                      try { await trustAPI.unbanUser({ userId: user._id }); toast.success('User unbanned'); }
                      catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
                    }} style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#2d6a2d', color: 'white', border: 'none', cursor: 'pointer' }}>
                      Unban User
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ── ADMIN DELIVERY ZONES ──────────────────────────────────────────────────────
function AdminDeliveryPage() {
  const [zones,   setZones]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | zone object
  const [form,    setForm]    = useState({ type: 'city', value: '', label: '', charge: 0, freeAbove: '', note: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await deliveryAPI.getZones();
      setZones(data.zones || []);
    } catch { toast.error('Failed to load zones'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => {
    setForm({ type: 'city', value: '', label: '', charge: 0, freeAbove: '', note: '' });
    setEditing('new');
  };
  const openEdit = (zone) => {
    setForm({ type: zone.type, value: zone.value || '', label: zone.label || '', charge: zone.charge, freeAbove: zone.freeAbove || '', note: zone.note || '' });
    setEditing(zone);
  };

  const save = async () => {
    const body = { ...form, charge: Number(form.charge), freeAbove: form.freeAbove ? Number(form.freeAbove) : null };
    try {
      if (editing === 'new') {
        await deliveryAPI.create(body);
        toast.success('Zone created');
      } else {
        await deliveryAPI.update(editing._id, body);
        toast.success('Zone updated');
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this zone?')) return;
    try {
      await deliveryAPI.delete(id);
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  };

  const toggle = async (zone) => {
    try {
      await deliveryAPI.update(zone._id, { isActive: !zone.isActive });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Toggle failed'); }
  };

  const typeLabel = { pincode: 'Pincode', city: 'City', default: 'Default' };
  const typeColor = { pincode: '#0277bd', city: '#5e35b1', default: '#2d6a2d' };

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={s.pageTitle}>Delivery Zones</h2>
          <p className="text-muted text-sm mt-4">Set delivery charges by city or pincode. More specific rules take priority.</p>
        </div>
        <button onClick={openNew} className="btn btn-gold">+ Add Zone</button>
      </div>

      {/* Info box */}
      <div style={{ padding: '12px 16px', background: 'var(--cream)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.8 }}>
        <strong style={{ color: 'var(--ink)' }}>Priority order:</strong> Pincode (exact match) → City (name match) → Default (fallback for everywhere else)
      </div>

      {loading ? <Spinner centered /> : (
        <div className="admin-scrolltable" style={{ background: 'white', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Type', 'Value', 'Label', 'Charge', 'Free Above', 'Status', 'Actions'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {zones.length === 0 && (
                <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: 'var(--muted)', padding: 32 }}>No zones configured. Add a default zone to set a base delivery charge.</td></tr>
              )}
              {zones.map(zone => (
                <tr key={zone._id} style={{ opacity: zone.isActive ? 1 : 0.5 }}>
                  <td style={s.td}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: typeColor[zone.type] + '18', border: `1px solid ${typeColor[zone.type]}44`, color: typeColor[zone.type] }}>
                      {typeLabel[zone.type]}
                    </span>
                  </td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 13 }}>{zone.value || '—'}</td>
                  <td style={{ ...s.td, fontSize: 12 }}>{zone.label || '—'}</td>
                  <td style={{ ...s.td, fontWeight: 700 }}>₹{zone.charge}</td>
                  <td style={{ ...s.td, fontSize: 12 }}>{zone.freeAbove ? `Free above ₹${zone.freeAbove}` : '—'}</td>
                  <td style={s.td}>
                    <button onClick={() => toggle(zone)} style={{ padding: '3px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', border: '1px solid', cursor: 'pointer', background: zone.isActive ? '#e8f5e9' : '#fafafa', borderColor: zone.isActive ? '#4caf50' : '#9e9e9e', color: zone.isActive ? '#1b5e20' : '#616161' }}>
                      {zone.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={s.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEdit(zone)} className="btn btn-ghost btn-sm">Edit</button>
                      <button onClick={() => del(zone._id)} className="btn btn-ghost btn-sm" style={{ color: '#b71c1c' }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / New modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', width: '100%', maxWidth: 480, padding: 28 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, marginBottom: 20 }}>
              {editing === 'new' ? 'New Delivery Zone' : 'Edit Zone'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Zone Type</label>
                <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value, value: '' }))}>
                  <option value="city">City</option>
                  <option value="pincode">Pincode</option>
                  <option value="default">Default (fallback)</option>
                </select>
              </div>
              {form.type !== 'default' && (
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>
                    {form.type === 'pincode' ? 'Pincode (6 digits)' : 'City Name'}
                  </label>
                  <input className="input" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                    placeholder={form.type === 'pincode' ? '144001' : 'Jalandhar'} />
                </div>
              )}
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Label (shown to customer)</label>
                <input className="input" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Jalandhar City, NIT Area" />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Delivery Charge (₹)</label>
                  <input className="input" type="number" min="0" value={form.charge} onChange={e => setForm(f => ({ ...f, charge: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Free Delivery Above (₹)</label>
                  <input className="input" type="number" min="0" value={form.freeAbove} onChange={e => setForm(f => ({ ...f, freeAbove: e.target.value }))} placeholder="Leave blank to never give free delivery" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Admin Note (optional)</label>
                <input className="input" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Internal note" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button>
              <button onClick={save} className="btn btn-gold">Save Zone</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ── Shared mini-badge components ──────────────────────────────────────────────
function Notice({ children, color = 'var(--muted)', style: extraStyle }) {
  return (
    <div style={{ borderLeft: '3px solid ' + color, padding: '10px 14px', background: 'var(--cream)', fontSize: 12, color: 'var(--ink)', lineHeight: 1.7, ...extraStyle }}>
      {children}
    </div>
  );
}

function TypeBadge({ type }) {
  const isPurchase = type === 'purchase';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', fontSize: 9, fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      background: isPurchase ? '#f3e5f5' : '#e3f2fd',
      border: `1px solid ${isPurchase ? '#9c27b0' : '#2196f3'}`,
      color: isPurchase ? '#4a148c' : '#0d47a1',
    }}>
      {isPurchase ? 'Purchase' : 'Rental'}
    </span>
  );
}

function PayMethodBadge({ method }) {
  const isCod = method === 'cod';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', fontSize: 9, fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      background: isCod ? '#fff8e1' : '#e8f5e9',
      border: `1px solid ${isCod ? '#ffc107' : '#4caf50'}`,
      color: isCod ? '#856404' : '#1b5e20',
    }}>
      {isCod ? 'COD' : 'Online'}
    </span>
  );
}

function PayStatusBadge({ status }) {
  const map = {
    paid:        { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20', label: 'Paid' },
    pending_cod: { bg: '#fff8e1', border: '#ffc107', text: '#856404', label: 'Pending COD' },
    pending:     { bg: '#fafafa', border: '#9e9e9e', text: '#424242', label: 'Pending' },
    refunded:       { bg: '#e3f2fd', border: '#2196f3', text: '#0d47a1', label: 'Refunded' },
    refund_pending: { bg: '#fff3e0', border: '#ff9800', text: '#e65100', label: '⚠ Refund Pending' },
    failed:         { bg: '#ffebee', border: '#f44336', text: '#b71c1c', label: 'Failed' },
  };
  const c = map[status] || map.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', fontSize: 9, fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
    }}>
      {c.label}
    </span>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', borderBottom: '1px solid rgba(184,146,74,0.2)', paddingBottom: 6, marginTop: 24, marginBottom: 14 }}>
      {children}
    </div>
  );
}
function FormRow({ label, children }) {
  return (
    <div className="form-group" style={{ marginBottom: 14 }}>
      <label className="form-label" style={{ marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

// ── ADMIN PICKUPS PAGE ───────────────────────────────────────────────────────
function AdminPickups() {
  const [pickups,  setPickups]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchPickups = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getAllOrders({ status: 'Return Requested', limit: 100 });
      setPickups(data.orders || []);
    } catch { toast.error('Failed to load pickups'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPickups(); }, []);

  const SLOT_LABELS = { morning: '9 AM - 12 PM', afternoon: '12 PM - 4 PM', evening: '4 PM - 8 PM' };
  const SLOT_COLORS = { morning: '#fff8e1', afternoon: '#e3f2fd', evening: '#f3e5f5' };
  const SLOT_BORDER = { morning: '#ffc107', afternoon: '#2196f3', evening: '#9c27b0' };

  const grouped = {};
  const noDate  = [];
  pickups.forEach(o => {
    if (o.pickupDate) {
      const d = new Date(o.pickupDate).toISOString().split('T')[0];
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(o);
    } else { noDate.push(o); }
  });

  const allDates = Object.keys(grouped).sort();
  const today    = new Date().toISOString().split('T')[0];
  const upcoming = allDates.filter(d => d >= today);
  const past     = allDates.filter(d => d < today);

  const renderCard = (order) => (
    <div key={order._id} onClick={() => setSelected(order)}
      style={{ background: 'white', border: '1px solid var(--border)', borderLeft: '3px solid ' + (SLOT_BORDER[order.pickupSlot] || 'var(--gold)'), padding: '14px 18px', marginBottom: 10, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>#{order.orderId}</span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 8px', background: SLOT_COLORS[order.pickupSlot] || '#f5f5f5', border: '1px solid ' + (SLOT_BORDER[order.pickupSlot] || '#ddd'), color: SLOT_BORDER[order.pickupSlot] || '#666', textTransform: 'uppercase' }}>
              {SLOT_LABELS[order.pickupSlot] || 'No time specified'}
            </span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>{order.user?.name}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{order.user?.phone || order.user?.email}</div>
          {order.shippingAddress && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              {order.shippingAddress.street}, {order.shippingAddress.city} - {order.shippingAddress.pincode}
            </div>
          )}
          {order.returnNote && <div style={{ fontSize: 11, color: '#b8860b', marginTop: 4, fontStyle: 'italic' }}>Note: {order.returnNote}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: 'var(--ink)' }}>
            {order.items?.length} item{order.items?.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fade-up">

      {/* Detail drawer */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setSelected(null)}>
          <div style={{ width: 'min(480px, 100vw)', background: '#fff', height: '100%', overflowY: 'auto', padding: 28, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300 }}>Pickup Details</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--muted)' }}>x</button>
            </div>
            <div style={{ background: 'var(--ink)', color: 'white', padding: '16px 20px', marginBottom: 20 }}>
              <div style={{ fontSize: 8, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Order</div>
              <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700 }}>#{selected.orderId}</div>
            </div>

            <div style={{ background: SLOT_COLORS[selected.pickupSlot] || '#f5f5f5', border: '1px solid ' + (SLOT_BORDER[selected.pickupSlot] || '#ddd'), padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Scheduled Pickup</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
                {selected.pickupDate ? new Date(selected.pickupDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date not specified'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{SLOT_LABELS[selected.pickupSlot] || 'Time not specified'}</div>
              {selected.returnNote && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(0,0,0,0.05)', fontSize: 12, fontStyle: 'italic', color: 'var(--muted)' }}>"{selected.returnNote}"</div>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Customer</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{selected.user?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>{selected.user?.email}</div>
              {selected.user?.phone && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Phone: {selected.user.phone}</div>}
            </div>

            {selected.shippingAddress && (
              <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', padding: '14px 18px', marginBottom: 20 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Pickup Address</div>
                <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                  <strong>{selected.shippingAddress.name}</strong><br />
                  {selected.shippingAddress.street}<br />
                  {selected.shippingAddress.city}, {selected.shippingAddress.state} - {selected.shippingAddress.pincode}<br />
                  Phone: {selected.shippingAddress.phone}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Items to Collect</div>
              {selected.items?.map((item, i) => (
                <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid var(--cream-dark)', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{item.productSnapshot?.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Size {item.size} - {item.rentalDurationLabel}</div>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                    Return by {item.returnDate ? new Date(item.returnDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-'}
                  </div>
                </div>
              ))}
            </div>

            <button onClick={() => setSelected(null)} style={{ width: '100%', padding: '11px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid var(--border)', paddingBottom: 20 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>Warbadobe Rentals</div>
          <h2 style={s.pageTitle}>Pickup Schedule</h2>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: 'var(--ink)', lineHeight: 1 }}>{pickups.length}</div>
          <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 3 }}>Pending Pickups</div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)', fontSize: 13 }}>Loading pickups...</div>
      ) : upcoming.length === 0 && noDate.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', background: 'white', border: '1px solid var(--border)' }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontStyle: 'italic', fontWeight: 300, color: 'var(--cream-dark)', marginBottom: 8 }}>No pickups scheduled</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Customers who schedule returns will appear here with their date and time slot</div>
        </div>
      ) : (
        <div>
          {upcoming.map(date => {
            const isToday    = date === today;
            const isTomorrow = date === new Date(Date.now() + 86400000).toISOString().split('T')[0];
            const label      = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : new Date(date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
            const dayOrders  = [...grouped[date]].sort((a, b) => ({ morning:0, afternoon:1, evening:2 }[a.pickupSlot] ?? 3) - ({ morning:0, afternoon:1, evening:2 }[b.pickupSlot] ?? 3));
            return (
              <div key={date} style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: isToday ? 600 : 300, color: isToday ? 'var(--ink)' : 'var(--muted)' }}>{label}</div>
                  {isToday && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', padding: '3px 10px', background: 'var(--ink)', color: 'var(--gold)', textTransform: 'uppercase' }}>Today</span>}
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{dayOrders.length} pickup{dayOrders.length !== 1 ? 's' : ''}</span>
                </div>
                {dayOrders.map(renderCard)}
              </div>
            );
          })}

          {noDate.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, color: '#b8860b' }}>Date Not Specified</div>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{noDate.length} order{noDate.length !== 1 ? 's' : ''}</span>
              </div>
              {noDate.map(renderCard)}
            </div>
          )}

          {past.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16, userSelect: 'none' }}>
                Past Scheduled Pickups ({past.reduce((n, d) => n + grouped[d].length, 0)})
              </summary>
              <div style={{ marginTop: 16 }}>
                {past.map(date => (
                  <div key={date} style={{ marginBottom: 20, opacity: 0.6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 10 }}>
                      {new Date(date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                    {grouped[date].map(renderCard)}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


// ── Styles ────────────────────────────────────────────────────────────────────
// ── ADMIN RENTALS — Active rentals & overdue tracker ─────────────────────────
function AdminRentals() {
  const [rentals,  setRentals]  = useState([]);
  const [overdue,  setOverdue]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('active'); // 'active' | 'overdue'
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [activeRes, overdueRes] = await Promise.all([
        adminAPI.getAllOrders({ status: 'Active Rental', limit: 100 }),
        adminAPI.getAllOrders({ status: 'Active Rental', overdue: 'true', limit: 100 }),
      ]);
      const all = activeRes.data.orders || [];
      const now = new Date();
      const overdueList = all.filter(o =>
        o.items?.some(item => item.returnDate && new Date(item.returnDate) < now)
      );
      const activeList  = all.filter(o =>
        !o.items?.some(item => item.returnDate && new Date(item.returnDate) < now)
      );
      setRentals(activeList);
      setOverdue(overdueList);
    } catch { toast.error('Failed to load rentals'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const quickStatus = async (orderId, status) => {
    setUpdating(orderId);
    try {
      await adminAPI.updateOrderStatus(orderId, { status });
      toast.success('Updated to ' + status);
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setUpdating(null); }
  };

  const displayed = tab === 'active' ? rentals : overdue;

  const daysUntilReturn = (order) => {
    const d = order.items?.[0]?.returnDate;
    if (!d) return null;
    return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  };

  if (loading) return <Spinner size="lg" centered />;

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={s.pageTitle}>Active Rentals</h2>
          <p className="text-muted text-sm mt-4">{rentals.length} active · {overdue.length} overdue</p>
        </div>
        <button onClick={load} style={{ padding:'8px 14px', background:'white', border:'1px solid var(--border)', cursor:'pointer', fontSize:11, color:'var(--muted)' }}>↻ Refresh</button>
      </div>

      {/* Overdue alert */}
      {overdue.length > 0 && (
        <div style={{ background:'#fff5f5', border:'1px solid #f44336', padding:'12px 16px', marginBottom:20, display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ width:3, background:'#c62828', alignSelf:'stretch', flexShrink:0 }} />
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#b71c1c' }}>⚠ {overdue.length} Overdue Return{overdue.length > 1 ? 's' : ''}</div>
            <div style={{ fontSize:12, color:'#c62828', marginTop:2 }}>These customers have not returned their outfits past the due date.</div>
          </div>
          <button onClick={() => setTab('overdue')} style={{ padding:'6px 14px', fontSize:10, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', background:'#b71c1c', color:'white', border:'none', cursor:'pointer' }}>
            View Overdue
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, background:'white', border:'1px solid var(--border)', padding:3, marginBottom:20, width:'fit-content' }}>
        {[['active', `Active (${rentals.length})`], ['overdue', `Overdue (${overdue.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding:'7px 20px', fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', border:'none', cursor:'pointer',
              background: tab === key ? (key === 'overdue' ? '#b71c1c' : 'var(--ink)') : 'none',
              color: tab === key ? (key === 'overdue' ? 'white' : 'var(--gold)') : 'var(--muted)' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Rentals table */}
      {displayed.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'var(--muted)', background:'white', border:'1px solid var(--border)' }}>
          {tab === 'active' ? 'No active rentals right now.' : '✓ No overdue rentals!'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {displayed.map(order => {
            const days  = daysUntilReturn(order);
            const retDate = order.items?.[0]?.returnDate;
            const isOD  = days !== null && days < 0;
            const urgnt = days !== null && days <= 1 && days >= 0;
            return (
              <div key={order._id} style={{ background:'white', border:`1px solid ${isOD ? '#f44336' : urgnt ? '#ffc107' : 'var(--border)'}`, borderLeft:`3px solid ${isOD ? '#c62828' : urgnt ? '#ffc107' : 'var(--gold)'}`, padding:'16px 20px', display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>

                {/* Order info */}
                <div style={{ minWidth:0, flex:'1 1 200px' }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4, flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700 }}>#{order.orderId}</span>
                    {isOD && <span style={{ fontSize:9, fontWeight:800, padding:'2px 8px', background:'#ffebee', border:'1px solid #f44336', color:'#b71c1c', letterSpacing:'0.1em', textTransform:'uppercase' }}>OVERDUE {Math.abs(days)}d</span>}
                    {urgnt && <span style={{ fontSize:9, fontWeight:800, padding:'2px 8px', background:'#fff8e1', border:'1px solid #ffc107', color:'#856404', letterSpacing:'0.1em', textTransform:'uppercase' }}>DUE SOON</span>}
                  </div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{order.user?.name}</div>
                  <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{order.user?.phone ? `+91${order.user.phone}` : order.user?.email}</div>
                </div>

                {/* Items */}
                <div style={{ flex:'1 1 160px' }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', marginBottom:5 }}>Items</div>
                  {order.items?.map((item, i) => (
                    <div key={i} style={{ fontSize:11, color:'var(--ink)', marginBottom:2 }}>
                      {item.productSnapshot?.name} <span style={{ color:'var(--muted)' }}>· Size {item.size}</span>
                    </div>
                  ))}
                </div>

                {/* Return date */}
                <div style={{ flex:'0 0 140px', textAlign:'right' }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', marginBottom:5 }}>Return Date</div>
                  <div style={{ fontSize:13, fontWeight:700, color: isOD ? '#b71c1c' : urgnt ? '#856404' : 'var(--ink)' }}>
                    {retDate ? format(new Date(retDate), 'dd MMM yyyy') : '—'}
                  </div>
                  {days !== null && (
                    <div style={{ fontSize:10, color: isOD ? '#b71c1c' : 'var(--muted)', marginTop:2, fontWeight: isOD ? 700 : 400 }}>
                      {isOD ? `${Math.abs(days)} days overdue` : days === 0 ? 'Due today' : `${days} day${days !== 1 ? 's' : ''} left`}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ flex:'0 0 auto', display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
                  {order.user?.phone && (
                    <a href={`https://wa.me/91${order.user.phone.replace(/[^0-9]/g,'')}?text=Hi%20${encodeURIComponent(order.user?.name?.split(' ')[0]||'')}%2C%20this%20is%20Warbadobe.%20Please%20return%20your%20outfit%20(Order%20%23${order.orderId})${isOD ? '%20%E2%80%94%20it%20is%20overdue!' : '%20by%20the%20due%20date.'}`}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize:9, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'#25D366', textDecoration:'none', border:'1px solid #25D366', padding:'5px 10px', display:'inline-flex', alignItems:'center', gap:4 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Send Reminder
                    </a>
                  )}
                  <button onClick={() => quickStatus(order._id, 'Return Requested')} disabled={updating === order._id}
                    style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', padding:'5px 10px', background:'var(--ink)', color:'var(--gold)', border:'none', cursor:'pointer', opacity: updating===order._id ? 0.6 : 1 }}>
                    {updating === order._id ? '...' : 'Mark Return Req.'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  panel:           { display: 'flex', minHeight: '100vh', background: 'var(--cream)' },
  sidebar:         { width: 240, background: 'var(--ink)', flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100vh', overflowY: 'auto' },
  sidebarLogo:     { padding: '28px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  logoText:        { fontFamily: "'Cormorant Garamond', serif", color: 'var(--gold)', fontSize: 20, letterSpacing: '0.06em' },
  logoSub:         { fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 4 },
  navItem:         { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'all 0.15s', position: 'relative', borderLeft: '2px solid transparent' },
  navActive:       { background: 'rgba(184,146,74,0.08)', color: 'var(--gold)', borderLeft: '2px solid var(--gold)', paddingLeft: '22px' },
  badge:           { background: '#c62828', color: 'white', fontSize: 9, fontWeight: 800, padding: '2px 6px', letterSpacing: '0.06em' },
  main:            { flex: 1, padding: 36, minWidth: 0, boxSizing: 'border-box' },
  pageTitle:       { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(22px,4vw,38px)' },
  kpiGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 32 },
  kpiCard:         { background: 'white', border: '1px solid var(--border)', padding: '20px 22px', transition: 'box-shadow 0.2s' },
  card:            { background: 'white', border: '1px solid var(--border)', padding: 24 },
  cardTitle:       { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 14 },
  filterBar:       { background: 'white', border: '1px solid var(--border)', padding: '16px 20px', marginBottom: 20 },
  filterGroup:     { display: 'flex', flexDirection: 'column', gap: 8 },
  filterLabel:     { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' },
  filterChip:      { padding: '5px 12px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', background: 'white', color: 'var(--muted)', cursor: 'pointer', letterSpacing: '0.04em', transition: 'all 0.15s' },
  filterChipActive:{ background: 'var(--ink)', border: '1px solid var(--ink)', color: 'var(--gold)' },
  th:              { fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid var(--border)', background: 'var(--cream)', whiteSpace: 'nowrap' },
  td:              { padding: '11px 14px', fontSize: 13, borderBottom: '1px solid rgba(184,146,74,0.08)', verticalAlign: 'middle' },
};

const sp = {
  activeBadge:   { display: 'inline-block', padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#e8f5e9', border: '1px solid #4caf50', color: '#1b5e20' },
  inactiveBadge: { display: 'inline-block', padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#ffebee', border: '1px solid #f44336', color: '#b71c1c' },
  overlay:       { position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(14,11,8,0.6)', backdropFilter: 'blur(2px)', display: 'flex', justifyContent: 'flex-end' },
  drawer:        { width: '100%', maxWidth: 640, background: 'white', display: 'flex', flexDirection: 'column', height: '100vh', boxShadow: '-8px 0 40px rgba(0,0,0,0.2)' }, // .coupon-drawer added inline below
  drawerHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid var(--border)', background: 'var(--cream)', flexShrink: 0 },
  drawerBody:    { flex: 1, overflowY: 'auto', padding: '24px 28px' },
  drawerFoot:    { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 28px', borderTop: '1px solid var(--border)', background: 'var(--cream)', flexShrink: 0 },
  closeBtn:      { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--muted)', padding: 4 },
};

// ── ADMIN COUPONS ──────────────────────────────────────────────────────────────
function AdminCoupons() {
  const BLANK = {
    code: '', description: '', type: 'flat', value: '',
    minOrder: 0, maxDiscount: '',
    usageLimit: '', perUserLimit: 1,
    collegeOnly: false, targetCollege: '', targetCollegeKey: '',
    validFrom: '', validUntil: '',
    isActive: true,
  };

  const [coupons,    setCoupons]   = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [showForm,   setShowForm]  = useState(false);
  const [editing,    setEditing]   = useState(null); // null = create, id = edit
  const [form,       setForm]      = useState(BLANK);
  const [saving,     setSaving]    = useState(false);
  const [deleting,   setDeleting]  = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await couponAPI.getAll();
      setCoupons(data.coupons);
    } catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ ...BLANK, validFrom: new Date().toISOString().slice(0, 10) });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setForm({
      ...c,
      validFrom:   c.validFrom  ? new Date(c.validFrom).toISOString().slice(0,10)  : '',
      validUntil:  c.validUntil ? new Date(c.validUntil).toISOString().slice(0,10) : '',
      maxDiscount: c.maxDiscount ?? '',
      usageLimit:  c.usageLimit  ?? '',
    });
    setEditing(c._id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Coupon code is required'); return; }
    if (!form.value || Number(form.value) <= 0) { toast.error('Discount value must be > 0'); return; }
    if (form.type === 'percent' && Number(form.value) > 100) { toast.error('Percent discount cannot exceed 100'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        code:        form.code.toUpperCase().trim(),
        value:       Number(form.value),
        minOrder:    Number(form.minOrder) || 0,
        maxDiscount: form.maxDiscount !== '' ? Number(form.maxDiscount) : null,
        usageLimit:  form.usageLimit  !== '' ? Number(form.usageLimit)  : null,
        perUserLimit: Number(form.perUserLimit) || 1,
        validFrom:   form.validFrom  || null,
        validUntil:  form.validUntil || null,
        targetCollegeKey: form.targetCollegeKey.trim() || null,
        targetCollege:    form.targetCollege.trim()    || null,
      };
      if (editing) {
        await couponAPI.update(editing, payload);
        toast.success('Coupon updated');
      } else {
        await couponAPI.create(payload);
        toast.success('Coupon created');
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await couponAPI.delete(id);
      toast.success('Coupon deleted');
      load();
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleToggle = async (coup) => {
    try {
      await couponAPI.update(coup._id, { isActive: !coup.isActive });
      toast.success(coup.isActive ? 'Coupon deactivated' : 'Coupon activated');
      load();
    } catch { toast.error('Failed'); }
  };

  const isExpired = (c) => c.validUntil && new Date(c.validUntil) < new Date();
  const isLimitReached = (c) => c.usageLimit !== null && c.usedCount >= c.usageLimit;

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={s.pageTitle}>Coupons</h2>
          <p className="text-muted text-sm mt-4">{coupons.length} coupon{coupons.length !== 1 ? 's' : ''} · Promo codes & fest discounts</p>
        </div>
        <button onClick={openCreate}
          style={{ padding: '10px 24px', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}>
          + New Coupon
        </button>
      </div>

      {/* Quick-create UTKANSH preset banner */}
      {!coupons.some(c => c.code === 'UTKANSH') && (
        <div className="utkansh-banner" style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderLeft: '4px solid #2e7d32', padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1b5e20' }}>🎓 UTKANSH Fest is coming!</div>
            <div style={{ fontSize: 12, color: '#2e7d32', marginTop: 3 }}>Create a college-exclusive coupon for UTKANSH attendees with one click.</div>
          </div>
          <button
            onClick={() => {
              const festEnd = new Date(); festEnd.setDate(festEnd.getDate() + 14);
              setForm({
                ...BLANK,
                code: 'UTKANSH',
                description: 'UTKANSH College Fest — Exclusive 15% off for students',
                type: 'percent',
                value: 15,
                maxDiscount: 500,
                minOrder: 500,
                usageLimit: '',
                perUserLimit: 1,
                collegeOnly: true,
                targetCollege: 'UTKANSH',
                targetCollegeKey: '',
                validFrom: new Date().toISOString().slice(0,10),
                validUntil: festEnd.toISOString().slice(0,10),
                isActive: true,
              });
              setEditing(null);
              setShowForm(true);
            }}
            style={{ padding: '9px 20px', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: '#2e7d32', color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Create UTKANSH Coupon →
          </button>
        </div>
      )}

      {loading ? <Spinner size="lg" centered /> : coupons.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🏷️</div>
          <p>No coupons yet. Create your first one!</p>
        </div>
      ) : (
        <div className="admin-scrolltable" style={{ background: 'white', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Code', 'Description', 'Discount', 'Min Order', 'Usage', 'Valid Until', 'Status', 'Actions'].map((h, hi) => (
                  <th key={h} className={[1,3,4].includes(hi) ? 'coupon-col-hide' : ''} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coupons.map(coup => {
                const expired = isExpired(coup);
                const limitHit = isLimitReached(coup);
                const effectivelyActive = coup.isActive && !expired && !limitHit;
                return (
                  <tr key={coup._id} style={{ borderBottom: '1px solid var(--cream-dark)', opacity: effectivelyActive ? 1 : 0.65 }}>
                    <td style={s.td}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, letterSpacing: '0.08em', color: 'var(--ink)' }}>{coup.code}</span>
                      {coup.collegeOnly && (
                        <div style={{ fontSize: 9, color: '#1565c0', fontWeight: 700, marginTop: 2, letterSpacing: '0.06em' }}>
                          🎓 {coup.targetCollege || 'COLLEGE ONLY'}
                        </div>
                      )}
                    </td>
                    <td style={{ ...s.td, maxWidth: 200, fontSize: 12, color: 'var(--muted)' }}>
                      {coup.description || '—'}
                    </td>
                    <td style={s.td}>
                      <span style={{ fontWeight: 700, color: 'var(--gold)', fontSize: 14 }}>
                        {coup.type === 'flat' ? `₹${coup.value}` : `${coup.value}%`}
                      </span>
                      {coup.type === 'percent' && coup.maxDiscount && (
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>max ₹{coup.maxDiscount}</div>
                      )}
                    </td>
                    <td style={{ ...s.td, fontSize: 12 }}>
                      {coup.minOrder > 0 ? `₹${coup.minOrder.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontSize: 12 }}>
                        {coup.usedCount} used
                        {coup.usageLimit !== null && <span style={{ color: 'var(--muted)' }}> / {coup.usageLimit}</span>}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>max {coup.perUserLimit}/user</div>
                    </td>
                    <td style={{ ...s.td, fontSize: 12 }}>
                      {coup.validUntil ? (
                        <span style={{ color: expired ? '#b71c1c' : 'var(--ink)' }}>
                          {format(new Date(coup.validUntil), 'dd MMM yyyy')}
                          {expired && <div style={{ fontSize: 10, color: '#b71c1c', fontWeight: 700 }}>EXPIRED</div>}
                        </span>
                      ) : <span style={{ color: 'var(--muted)' }}>Never</span>}
                    </td>
                    <td style={s.td}>
                      {limitHit ? (
                        <span style={{ ...sp.inactiveBadge }}>LIMIT HIT</span>
                      ) : expired ? (
                        <span style={{ ...sp.inactiveBadge }}>EXPIRED</span>
                      ) : (
                        <span style={{ ...(coup.isActive ? sp.activeBadge : sp.inactiveBadge) }}>
                          {coup.isActive ? 'ACTIVE' : 'OFF'}
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(coup)}
                          style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 10px', background: 'none', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--muted)' }}>
                          Edit
                        </button>
                        <button onClick={() => handleToggle(coup)}
                          style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 10px', background: 'none', border: `1px solid ${coup.isActive ? '#f44336' : '#4caf50'}`, cursor: 'pointer', color: coup.isActive ? '#f44336' : '#4caf50' }}>
                          {coup.isActive ? 'Off' : 'On'}
                        </button>
                        <button onClick={() => handleDelete(coup._id)} disabled={deleting === coup._id}
                          style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 10px', background: '#ffebee', border: '1px solid #f44336', cursor: 'pointer', color: '#b71c1c' }}>
                          {deleting === coup._id ? '...' : 'Del'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Form Drawer ── */}
      {showForm && (
        <div style={sp.overlay} onClick={() => setShowForm(false)}>
          <div className="coupon-drawer" style={sp.drawer} onClick={e => e.stopPropagation()}>
            <div style={sp.drawerHead}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 400 }}>
                {editing ? 'Edit Coupon' : 'New Coupon'}
              </h2>
              <button style={sp.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={sp.drawerBody}>

              {/* Code + Type */}
              <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Coupon Code *</label>
                  <input className="input" style={{ textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: '0.08em', fontWeight: 700 }}
                    value={form.code} onChange={e => setF('code', e.target.value.toUpperCase())}
                    placeholder="e.g. UTKANSH" />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Discount Type *</label>
                  <select className="input" value={form.type} onChange={e => setF('type', e.target.value)}>
                    <option value="flat">Flat (₹ off)</option>
                    <option value="percent">Percent (% off)</option>
                  </select>
                </div>
              </div>

              {/* Value + Max Discount */}
              <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>
                    {form.type === 'flat' ? 'Discount Amount (₹) *' : 'Discount Percent (%) *'}
                  </label>
                  <input className="input" type="number" min="1" max={form.type === 'percent' ? 100 : undefined}
                    value={form.value} onChange={e => setF('value', e.target.value)}
                    placeholder={form.type === 'flat' ? '200' : '15'} />
                </div>
                {form.type === 'percent' && (
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Max Discount Cap (₹)</label>
                    <input className="input" type="number" min="0"
                      value={form.maxDiscount} onChange={e => setF('maxDiscount', e.target.value)}
                      placeholder="500 (blank = no cap)" />
                  </div>
                )}
              </div>

              {/* Description */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Description</label>
                <input className="input" value={form.description} onChange={e => setF('description', e.target.value)}
                  placeholder="UTKANSH Fest — 15% off for college students" />
              </div>

              {/* Min order + Usage */}
              <div className="admin-form-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Min Order (₹)</label>
                  <input className="input" type="number" min="0"
                    value={form.minOrder} onChange={e => setF('minOrder', e.target.value)}
                    placeholder="0" />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Total Usage Limit</label>
                  <input className="input" type="number" min="1"
                    value={form.usageLimit} onChange={e => setF('usageLimit', e.target.value)}
                    placeholder="Blank = unlimited" />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Per User Limit</label>
                  <input className="input" type="number" min="1"
                    value={form.perUserLimit} onChange={e => setF('perUserLimit', e.target.value)}
                    placeholder="1" />
                </div>
              </div>

              {/* Validity dates */}
              <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Valid From</label>
                  <input className="input" type="date" value={form.validFrom} onChange={e => setF('validFrom', e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Valid Until</label>
                  <input className="input" type="date" value={form.validUntil} onChange={e => setF('validUntil', e.target.value)} />
                </div>
              </div>

              {/* College targeting */}
              <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: form.collegeOnly ? 14 : 0 }}>
                  <input type="checkbox" id="collegeOnly" checked={form.collegeOnly} onChange={e => setF('collegeOnly', e.target.checked)} style={{ width: 16, height: 16 }} />
                  <label htmlFor="collegeOnly" style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    🎓 College-verified users only
                  </label>
                </div>
                {form.collegeOnly && (
                  <div className="admin-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>College Display Name</label>
                      <input className="input" value={form.targetCollege}
                        onChange={e => setF('targetCollege', e.target.value)}
                        placeholder="e.g. UTKANSH" />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>College Keyword (to match)</label>
                      <input className="input" value={form.targetCollegeKey}
                        onChange={e => setF('targetCollegeKey', e.target.value)}
                        placeholder="utkansh (blank = any college)" />
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Matches user's college field (case-insensitive). Leave blank to allow any verified college.</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Active toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="isActive" checked={form.isActive} onChange={e => setF('isActive', e.target.checked)} style={{ width: 16, height: 16 }} />
                <label htmlFor="isActive" style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Active (visible to customers)</label>
              </div>

            </div>
            <div style={sp.drawerFoot}>
              <button onClick={() => setShowForm(false)}
                style={{ padding: '10px 20px', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--muted)' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '10px 28px', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}>
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}